// src/common.ts
import { z } from "zod";
var uuid = z.uuid();
var cents = z.number().int().min(0).max(1e10);
var signedCents = z.number().int().min(-1e10).max(1e10);
var timeHHmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hor\xE1rio inv\xE1lido (HH:mm)");
var isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inv\xE1lida (AAAA-MM-DD)");
var isoDateTime = z.iso.datetime({ offset: true });
var onlyDigits = (v) => v.replace(/\D/g, "");
var phoneBR = z.string().transform(onlyDigits).pipe(z.string().regex(/^\d{10,11}$/, "Telefone inv\xE1lido"));
var cepBR = z.string().transform(onlyDigits).pipe(z.string().regex(/^\d{8}$/, "CEP inv\xE1lido"));
function isValidCpf(raw) {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const calc = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(cpf[i]) * (len + 1 - i);
    const r = sum * 10 % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(cpf[9]) && calc(10) === Number(cpf[10]);
}
function isValidCnpj(raw) {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const calc = (len) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + w * Number(cnpj[i]), 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(cnpj[12]) && calc(13) === Number(cnpj[13]);
}
var cpfBR = z.string().transform(onlyDigits).refine(isValidCpf, "CPF inv\xE1lido");
var cnpjBR = z.string().transform(onlyDigits).refine(isValidCnpj, "CNPJ inv\xE1lido");
var optionalText = (max = 500) => z.string().trim().max(max).optional().nullable().transform((v) => v ? v : null);
var safeUrl = z.string().trim().max(2048).refine((v) => {
  if (v.startsWith("/") && !v.startsWith("//")) return true;
  if (v.startsWith("#")) return true;
  try {
    const u = new URL(v);
    return ["http:", "https:", "mailto:", "tel:"].includes(u.protocol);
  } catch {
    return false;
  }
}, "URL n\xE3o permitida");
var sortOrder = z.enum(["asc", "desc"]).default("asc");
var paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(20),
  q: z.string().trim().max(120).optional(),
  sort: z.string().max(40).optional(),
  order: sortOrder
});
var idempotencyKeyHeader = "idempotency-key";
var idempotencyKey = z.string().min(8).max(100);
var addressSchema = z.object({
  cep: cepBR,
  state: z.string().trim().length(2).toUpperCase(),
  city: z.string().trim().min(1).max(120),
  district: z.string().trim().min(1).max(120),
  street: z.string().trim().min(1).max(200),
  number: z.string().trim().min(1).max(20),
  complement: optionalText(120)
});

// src/permissions.ts
var PERMISSIONS = {
  "products.read": "Ver produtos",
  "products.write": "Cadastrar e editar produtos",
  "products.publish": "Publicar e despublicar produtos na loja",
  "inventory.read": "Ver estoque e movimenta\xE7\xF5es",
  "inventory.write": "Registrar entradas, sa\xEDdas e localiza\xE7\xE3o",
  "inventory.adjust": "Ajustar estoque e fazer invent\xE1rio f\xEDsico",
  "consignments.read": "Ver consigna\xE7\xF5es e consignantes",
  "consignments.write": "Registrar consigna\xE7\xF5es e consignantes",
  "consignments.settle": "Fechar acertos e registrar repasses a consignantes",
  "suppliers.read": "Ver fornecedores",
  "suppliers.write": "Cadastrar e editar fornecedores",
  "customers.read": "Ver dados cadastrais de clientes",
  "customers.write": "Cadastrar e editar clientes",
  "customers.export": "Exportar clientes",
  "orders.read": "Ver pedidos",
  "orders.write": "Criar e atualizar pedidos",
  "orders.manage": "Cancelar, devolver e gerir pedidos",
  "payments.read": "Ver pagamentos",
  "payments.write": "Registrar pagamentos",
  "payments.refund": "Estornar pagamentos",
  "returns.read": "Ver trocas e devolu\xE7\xF5es",
  "returns.write": "Abrir e acompanhar trocas e devolu\xE7\xF5es, conversar com o cliente",
  "returns.approve": "Aprovar, recusar e concluir devolu\xE7\xF5es (reembolso, cr\xE9dito, troca)",
  "returns.inspect": "Registrar recebimento e inspe\xE7\xE3o de pe\xE7as devolvidas",
  "pos.operate": "Operar o caixa (PDV)",
  "pos.discount": "Conceder desconto no PDV",
  "pos.cash_close": "Fechar o caixa",
  "finance.read": "Ver financeiro",
  "finance.write": "Lan\xE7ar despesas e receitas",
  "finance.manage": "Contas, categorias e fechamentos financeiros",
  "marketing.manage": "Gerenciar cupons, campanhas e promo\xE7\xF5es",
  "reports.read": "Ver relat\xF3rios",
  "settings.manage": "Configura\xE7\xF5es da loja",
  "members.manage": "Gerenciar membros, pap\xE9is e convites",
  "site.edit": "Editar a loja virtual e o site",
  "site.publish": "Publicar a loja virtual e o site",
  "storefront.edit": "Editar a loja virtual (apar\xEAncia, textos, banners, dom\xEDnio)",
  "storefront.publish": "Publicar e despublicar a loja virtual",
  "ai.use": "Usar assistente de IA",
  "ai.assistant": "Usar o assistente de IA do brech\xF3 (chat com indicadores)",
  "ai.product": "Usar a IA de cadastro de pe\xE7as",
  "audit.read": "Ver auditoria"
};
var ALL_PERMISSIONS = Object.keys(PERMISSIONS);
var DEFAULT_POS_DISCOUNT_LIMIT = {
  owner: 100,
  manager: 50,
  seller: 10,
  stockist: 0,
  finance: 0
};
var p = (...perms) => perms;
var DEFAULT_ROLES = {
  owner: { name: "Propriet\xE1rio", permissions: ALL_PERMISSIONS },
  manager: {
    name: "Gerente",
    permissions: ALL_PERMISSIONS.filter((x) => x !== "members.manage" && x !== "audit.read")
  },
  seller: {
    name: "Vendedor",
    permissions: p(
      "products.read",
      "inventory.read",
      "customers.read",
      "customers.write",
      "orders.read",
      "orders.write",
      "payments.read",
      "payments.write",
      "returns.read",
      "returns.write",
      "pos.operate",
      "pos.discount",
      "ai.use",
      "ai.assistant"
    )
  },
  stockist: {
    name: "Estoquista",
    permissions: p(
      "products.read",
      "products.write",
      "inventory.read",
      "inventory.write",
      "inventory.adjust",
      "consignments.read",
      "consignments.write",
      "returns.read",
      "returns.inspect",
      "suppliers.read",
      "suppliers.write",
      "ai.use",
      "ai.assistant",
      "ai.product"
    )
  },
  finance: {
    name: "Financeiro",
    permissions: p(
      "orders.read",
      "payments.read",
      "payments.write",
      "payments.refund",
      "returns.read",
      "returns.write",
      "returns.approve",
      "consignments.read",
      "consignments.settle",
      "finance.read",
      "finance.write",
      "finance.manage",
      "pos.cash_close",
      "reports.read",
      "ai.assistant"
    )
  }
};

// src/auth.ts
import { z as z2 } from "zod";
var acceptTermsField = z2.literal(true, { error: "\xC9 necess\xE1rio aceitar os Termos de Uso e a Pol\xEDtica de Privacidade" });
var passwordSchema = z2.string().min(8, "M\xEDnimo de 8 caracteres").max(128).regex(/[A-Za-z]/, "Inclua ao menos uma letra").regex(/\d/, "Inclua ao menos um n\xFAmero");
var emailSchema = z2.email("E-mail inv\xE1lido").trim().toLowerCase().max(254);
var loginInput = z2.object({ email: emailSchema, password: z2.string().min(1).max(128) });
var registerInput = z2.object({
  name: z2.string().trim().min(2).max(120),
  email: emailSchema,
  password: passwordSchema,
  organizationName: z2.string().trim().min(2).max(120),
  timezone: z2.string().default("America/Sao_Paulo"),
  acceptTerms: acceptTermsField
});
var forgotPasswordInput = z2.object({ email: emailSchema });
var resetPasswordInput = z2.object({ token: z2.string().min(20).max(200), password: passwordSchema });
var acceptInviteInput = z2.object({
  token: z2.string().min(20).max(200),
  name: z2.string().trim().min(2).max(120).optional(),
  password: passwordSchema.optional(),
  acceptTerms: acceptTermsField
});
var acceptTermsInput = z2.object({ version: z2.string().min(1).max(20) });
var changePasswordInput = z2.object({ currentPassword: z2.string().min(1, "Informe a senha atual").max(128), newPassword: passwordSchema });
var updateProfileInput = z2.object({ name: z2.string().trim().min(2, "Informe ao menos 2 caracteres").max(120, "M\xE1ximo de 120 caracteres") });
var changeEmailInput = z2.object({
  currentPassword: z2.string().min(1, "Informe a senha atual").max(128),
  newEmail: emailSchema
});
var inviteMemberInput = z2.object({
  email: emailSchema,
  roleId: z2.uuid()
});
var updateMemberInput = z2.object({
  roleId: z2.uuid().optional(),
  active: z2.boolean().optional()
});
var roleInput = z2.object({
  name: z2.string().trim().min(2).max(60),
  permissions: z2.array(z2.string()).max(100),
  description: optionalText(200)
});

// src/org.ts
import { z as z3 } from "zod";
var slugSchema = z3.string().trim().toLowerCase().min(3).max(60).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use letras min\xFAsculas, n\xFAmeros e h\xEDfens");
var organizationSettingsInput = z3.object({
  name: z3.string().trim().min(2).max(120),
  slug: slugSchema,
  cnpj: cnpjBR.optional().nullable(),
  phone: phoneBR.optional().nullable(),
  whatsapp: phoneBR.optional().nullable(),
  email: z3.email().optional().nullable(),
  timezone: z3.string().min(3).max(60),
  instagram: safeUrl.optional().nullable(),
  facebook: safeUrl.optional().nullable(),
  tiktok: safeUrl.optional().nullable(),
  brandPrimary: z3.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  brandAccent: z3.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  logoAssetId: z3.uuid().nullable().optional(),
  faviconAssetId: z3.uuid().nullable().optional(),
  /** libera dados identificáveis (de clientes com consentimento `ia_externa`) ao provedor externo de IA */
  aiExternalEnabled: z3.boolean().optional()
});
var branchInput = z3.object({
  name: z3.string().trim().min(2).max(120),
  phone: phoneBR.optional().nullable(),
  address: addressSchema,
  active: z3.boolean().default(true)
});
var weekday = z3.number().int().min(0).max(6);
var businessHoursInterval = z3.object({ weekday, opensAt: timeHHmm, closesAt: timeHHmm }).refine((v) => v.opensAt < v.closesAt, "Abertura deve ser antes do fechamento");
var businessHoursInput = z3.object({
  branchId: z3.uuid(),
  intervals: z3.array(businessHoursInterval).max(50)
});
var cepLookupResponse = z3.object({
  cep: z3.string(),
  state: z3.string(),
  city: z3.string(),
  district: z3.string(),
  street: z3.string()
});
var onboardingStatus = z3.object({
  hasBranch: z3.boolean(),
  hasHours: z3.boolean(),
  sitePublished: z3.boolean()
});

// src/site.ts
import { z as z4 } from "zod";
var HTML_LIKE = /<\s*[a-zA-Z!/?]|&#?\w+;/;
var noHtml = (v) => !HTML_LIKE.test(v);
var NO_HTML_MSG = "Use apenas texto puro (sem HTML)";
var text = (max) => z4.string().trim().max(max).refine(noHtml, NO_HTML_MSG);
var siteUrl = safeUrl.refine((v) => !v.includes("\\"), "URL inv\xE1lida");
var assetRef = z4.object({ assetId: z4.uuid().nullable().optional(), url: siteUrl.nullable().optional(), alt: text(200).default("") }).refine((v) => !(v.assetId || v.url) || v.alt.length > 0, { message: "Informe o texto alternativo (alt) da imagem", path: ["alt"] });
var cta = z4.object({ label: text(40).min(1), href: siteUrl });
var base = { id: z4.string().regex(/^[A-Za-z0-9_-]{1,40}$/), hidden: z4.boolean().default(false) };
var heroBlock = z4.object({
  ...base,
  type: z4.literal("hero"),
  eyebrow: text(60).default(""),
  title: text(120).min(1),
  subtitle: text(300).default(""),
  image: assetRef.optional(),
  primaryCta: cta.optional(),
  secondaryCta: cta.optional()
});
var galleryBlock = z4.object({
  ...base,
  type: z4.literal("gallery"),
  title: text(120).default("Galeria"),
  images: z4.array(assetRef).max(24).default([])
});
var reviewsBlock = z4.object({
  ...base,
  type: z4.literal("reviews"),
  title: text(120).default("Depoimentos"),
  /** só avaliações com autorização de publicação */
  maxItems: z4.number().int().min(1).max(12).default(6)
});
var locationBlock = z4.object({
  ...base,
  type: z4.literal("location"),
  title: text(120).default("Onde estamos"),
  branchId: z4.uuid().nullable().optional(),
  showHours: z4.boolean().default(true),
  showMap: z4.boolean().default(true)
});
var ctaBlock = z4.object({
  ...base,
  type: z4.literal("cta"),
  title: text(120).min(1),
  body: text(500).default(""),
  button: cta
});
var textBlock = z4.object({
  ...base,
  type: z4.literal("text"),
  title: text(120).default(""),
  /** texto puro; quebras de linha viram parágrafos. Sem HTML. */
  body: text(5e3).default("")
});
var faqBlock = z4.object({
  ...base,
  type: z4.literal("faq"),
  title: text(120).default("Perguntas frequentes"),
  items: z4.array(z4.object({ q: text(200).min(1), a: text(1e3).min(1) })).max(20).default([])
});
var contactFormBlock = z4.object({
  ...base,
  type: z4.literal("contact_form"),
  title: text(120).default("Fale com a gente"),
  subtitle: text(300).default(""),
  successMessage: text(300).default("Mensagem enviada. Retornaremos em breve."),
  successPageSlug: slugSchema.nullable().optional()
});
var siteBlock = z4.discriminatedUnion("type", [
  heroBlock,
  galleryBlock,
  reviewsBlock,
  locationBlock,
  ctaBlock,
  textBlock,
  faqBlock,
  contactFormBlock
]);
var seoSchema = z4.object({
  title: text(70).default(""),
  description: text(160).default(""),
  ogImage: assetRef.nullable().optional(),
  noindex: z4.boolean().default(false)
});
var pageContentSchema = z4.object({
  blocks: z4.array(siteBlock).max(40).refine((b) => new Set(b.map((x) => x.id)).size === b.length, "Ids de bloco duplicados"),
  seo: seoSchema
});
var pageSlug = z4.union([z4.literal(""), slugSchema]);
var createPageInput = z4.object({ title: text(120).min(1), slug: pageSlug, showInNav: z4.boolean().default(true) });
var updatePageMetaInput = z4.object({
  title: text(120).min(1).optional(),
  slug: pageSlug.optional(),
  showInNav: z4.boolean().optional(),
  position: z4.number().int().min(0).optional()
});
var reorderPagesInput = z4.object({ ids: z4.array(z4.uuid()).min(1).max(100) });
var saveDraftInput = z4.object({ content: pageContentSchema, baseVersion: z4.number().int().min(0) });
var restoreVersionInput = z4.object({ baseVersion: z4.number().int().min(0).optional() });
var siteThemeInput = z4.object({
  primary: z4.string().regex(/^#[0-9a-fA-F]{6}$/),
  accent: z4.string().regex(/^#[0-9a-fA-F]{6}$/),
  background: z4.string().regex(/^#[0-9a-fA-F]{6}$/),
  fontPair: z4.enum(["classic", "modern", "editorial"]),
  logoAssetId: z4.uuid().nullable().optional(),
  faviconAssetId: z4.uuid().nullable().optional()
});
var contactMessageInput = z4.object({
  name: z4.string().trim().min(2).max(120).refine(noHtml, NO_HTML_MSG),
  email: z4.email().max(254),
  phone: z4.string().trim().max(20).optional(),
  message: z4.string().trim().min(5).max(2e3).refine(noHtml, NO_HTML_MSG),
  pageSlug: pageSlug.optional(),
  /** honeypot: humanos não preenchem; o servidor descarta em silêncio */
  website: z4.string().max(200).optional()
});
var contactMessagesQuery = z4.object({
  unread: z4.enum(["true", "false"]).optional(),
  page: z4.coerce.number().int().min(1).default(1),
  pageSize: z4.coerce.number().int().min(5).max(100).default(20)
});
var markMessageReadInput = z4.object({ read: z4.boolean().default(true) });
var reviewInput = z4.object({
  customerName: z4.string().trim().min(2).max(80).refine(noHtml, NO_HTML_MSG),
  rating: z4.number().int().min(1).max(5),
  comment: z4.string().trim().min(3).max(600).refine(noHtml, NO_HTML_MSG),
  publicationAuthorized: z4.boolean()
});

// src/notifications.ts
var NOTIFICATION_TYPES = [
  "invite",
  "password_reset",
  "email_changed",
  "order_confirmation",
  "order_reservation_reminder",
  "order_cancelled",
  "order_update",
  "financial_alert",
  "campaign",
  "contact_message",
  "portal_invite",
  "portal_password_reset",
  "storefront_alert"
];
var NOTIFICATION_STATUSES = [
  "queued",
  "sent",
  "failed",
  "skipped_unconfigured",
  "skipped_optout",
  "skipped_invalid_phone",
  "skipped_disabled",
  "skipped_limit"
];
var NOTIFICATION_CHANNELS = ["email", "whatsapp"];

// src/ai.ts
import { z as z5 } from "zod";
var aiChatInput = z5.object({
  messages: z5.array(z5.object({ role: z5.enum(["user", "assistant"]), content: z5.string().min(1).max(4e3) })).min(1).max(30)
});
var AI_DRAFT_KINDS = ["novidades", "retorno"];
var aiDraftInput = z5.object({
  kind: z5.enum(AI_DRAFT_KINDS),
  customerId: z5.uuid(),
  tone: z5.enum(["cordial", "objetivo", "empatico"]).default("cordial"),
  channel: z5.enum(["email", "whatsapp"]).default("whatsapp")
});
var aiSearchInput = z5.object({ question: z5.string().trim().min(3, "Escreva a pergunta").max(500) });

// src/ai-brecho.ts
import { z as z6 } from "zod";
var AI_PRODUCT_FIELDS = ["name", "description", "categoryId", "tags", "type", "department", "colorPrimary", "colorsSecondary", "defects"];
var aiAltField = (imageId) => `alt:${imageId}`;
var isAiAltField = (f) => f.startsWith("alt:");
var AI_FIELD_LABEL = {
  name: "T\xEDtulo comercial",
  description: "Descri\xE7\xE3o",
  categoryId: "Categoria",
  tags: "Tags, estilo e palavras-chave",
  type: "Tipo da pe\xE7a",
  department: "Departamento",
  colorPrimary: "Cor principal",
  colorsSecondary: "Cores secund\xE1rias",
  defects: "Poss\xEDveis defeitos vistos na foto"
};
var aiProductSuggestInput = z6.object({
  /** vazio = tudo; ou um subconjunto de blocos */
  only: z6.array(z6.enum(["fields", "social", "looks", "photo_tips", "alt_texts"])).max(5).optional()
});
var aiProductBatchInput = z6.object({ productIds: z6.array(uuid).min(1, "Selecione ao menos uma pe\xE7a").max(10, "No m\xE1ximo 10 pe\xE7as por vez") });
var aiFieldDecision = z6.object({
  field: z6.string().min(1).max(60),
  action: z6.enum(["accept", "edit", "reject"]),
  /** obrigatório em `edit`: texto, lista (tags/cores) ou id (categoria) */
  value: z6.union([z6.string().max(5e3), z6.array(z6.string().max(60)).max(20)]).optional()
});
var aiSuggestionReviewInput = z6.object({
  decisions: z6.array(aiFieldDecision).min(1).max(30),
  /** `confirm` = humano revisou e confirma; `draft` = grava marcando "sugerido por IA" até confirmação posterior (bloqueia publicar) */
  mode: z6.enum(["confirm", "draft"]).default("confirm")
});
var aiConfirmFieldsInput = z6.object({ fields: z6.array(z6.string().min(1).max(60)).min(1).max(40) });
var AI_PRODUCT_UNCONFIRMED_ERROR_CODE = "ai_unconfirmed_fields";
var AI_ACTION_KINDS = ["create_coupon", "create_promotion", "create_campaign_draft", "adjust_prices"];
var AI_ACTION_LABEL = {
  create_coupon: "Criar cupom",
  create_promotion: "Criar promo\xE7\xE3o",
  create_campaign_draft: "Criar campanha (rascunho)",
  adjust_prices: "Ajustar pre\xE7os em lote"
};
var aiConversationCreateInput = z6.object({ title: z6.string().trim().max(120).optional() });
var aiAssistantMessageInput = z6.object({ content: z6.string().trim().min(2, "Escreva a pergunta").max(2e3) });
var aiActionDecisionInput = z6.object({ note: z6.string().trim().max(300).optional() });
var aiAuditQuery = paginationQuery.extend({
  userId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});

// src/customers.ts
import { z as z7 } from "zod";
var customerAddressInput = addressSchema.extend({
  /** rótulo livre: Casa, Trabalho, Presente... */
  label: z7.string().trim().max(40).optional().nullable()
});
var customerInput = z7.object({
  name: z7.string().trim().min(2).max(120),
  cpf: cpfBR.optional().nullable(),
  birthDate: isoDate.optional().nullable(),
  phone: phoneBR.optional().nullable(),
  whatsapp: phoneBR.optional().nullable(),
  email: z7.email().max(254).optional().nullable(),
  /** true = nunca enviar mensagens de WhatsApp a este cliente */
  whatsappOptOut: z7.boolean().optional(),
  /** endereços de entrega/cobrança do cliente */
  addresses: z7.array(customerAddressInput).max(10).optional(),
  notes: optionalText(4e3),
  active: z7.boolean().default(true)
});
var boolQuery = z7.enum(["true", "false"]).transform((v) => v === "true");
var customerFilters = z7.object({
  q: z7.string().trim().max(120).optional(),
  active: boolQuery.optional(),
  /** true = lixeira (excluídos) */
  deleted: boolQuery.default(false)
});
var customerListQuery = paginationQuery.extend(customerFilters.shape);
var customerExportQuery = customerFilters;
var customerDuplicateQuery = z7.object({
  cpf: cpfBR.optional(),
  email: z7.email().max(254).optional(),
  phone: phoneBR.optional(),
  excludeId: z7.uuid().optional()
});
var CONSENT_TYPES = ["uso_de_dados", "comunicacao_whatsapp", "comunicacao_email", "marketing", "ia_externa"];
var consentType = z7.enum(CONSENT_TYPES);
var consentInput = z7.object({
  type: consentType,
  granted: z7.boolean(),
  /** versão do texto apresentado ao cliente */
  textVersion: z7.string().trim().min(1).max(40),
  notes: optionalText(500)
});

// src/portal.ts
import { z as z8 } from "zod";
var portalLoginInput = z8.object({
  organizationSlug: z8.string().trim().toLowerCase().min(1).max(60),
  email: emailSchema,
  password: z8.string().min(1).max(128)
});
var portalTokenInput = z8.object({ token: z8.string().min(20).max(200) });
var portalActivateInput = z8.object({ token: z8.string().min(20).max(200), password: passwordSchema });
var portalForgotPasswordInput = z8.object({
  organizationSlug: z8.string().trim().toLowerCase().min(1).max(60),
  email: emailSchema
});
var portalResetPasswordInput = z8.object({ token: z8.string().min(20).max(200), password: passwordSchema });
var portalProfileUpdateInput = z8.object({
  phone: phoneBR.nullable().optional(),
  whatsapp: phoneBR.nullable().optional(),
  addresses: z8.array(customerAddressInput).max(10).optional()
}).strict().refine((v) => Object.keys(v).length > 0, "Informe ao menos um campo");
var portalConsentInput = z8.object({
  type: consentType,
  granted: z8.boolean(),
  textVersion: z8.string().trim().min(1).max(40)
});
var portalCommunicationInput = z8.object({
  whatsapp: z8.boolean().optional(),
  email: z8.boolean().optional(),
  textVersion: z8.string().trim().min(1).max(40)
}).refine((v) => v.whatsapp !== void 0 || v.email !== void 0, "Informe ao menos um canal");
var portalInviteInput = z8.object({ email: emailSchema.optional() });
var PORTAL_ACCOUNT_STATUSES = ["convidado", "ativo", "revogado"];
var portalAccountsQuery = paginationQuery.pick({ page: true, pageSize: true }).extend({
  status: z8.enum(PORTAL_ACCOUNT_STATUSES).optional()
});

// src/dashboard.ts
import { z as z10 } from "zod";

// src/orders.ts
import { z as z9 } from "zod";
var ORDER_STATUSES = [
  "aguardando_pagamento",
  "pagamento_em_analise",
  "pago",
  "em_separacao",
  "pronto_para_retirada",
  "enviado",
  "entregue",
  "cancelado",
  "reembolsado",
  "em_devolucao"
];
var orderStatus = z9.enum(ORDER_STATUSES);
var ORDER_STATUS_LABELS = {
  aguardando_pagamento: "Aguardando pagamento",
  pagamento_em_analise: "Pagamento em an\xE1lise",
  pago: "Pago",
  em_separacao: "Em separa\xE7\xE3o",
  pronto_para_retirada: "Pronto para retirada",
  enviado: "Enviado",
  entregue: "Entregue",
  cancelado: "Cancelado",
  reembolsado: "Reembolsado",
  em_devolucao: "Em devolu\xE7\xE3o"
};
var ORDER_TRANSITIONS = {
  aguardando_pagamento: ["pagamento_em_analise", "pago", "cancelado"],
  pagamento_em_analise: ["aguardando_pagamento", "pago", "cancelado", "reembolsado"],
  pago: ["em_separacao", "pronto_para_retirada", "enviado", "reembolsado"],
  em_separacao: ["pronto_para_retirada", "enviado", "reembolsado"],
  pronto_para_retirada: ["entregue", "reembolsado", "em_devolucao"],
  enviado: ["entregue", "reembolsado", "em_devolucao"],
  entregue: ["em_devolucao", "reembolsado"],
  cancelado: [],
  reembolsado: [],
  em_devolucao: ["entregue", "reembolsado"]
};
function canOrderTransition(from, to) {
  return ORDER_TRANSITIONS[from].includes(to);
}
var ORDER_PAID_STATUSES = ["pago", "em_separacao", "pronto_para_retirada", "enviado", "entregue"];
var ORDER_OPEN_STATUSES = ["aguardando_pagamento", "pagamento_em_analise"];
var ORDER_CHANNELS = ["online", "pdv", "manual", "whatsapp", "instagram"];
var orderChannel = z9.enum(ORDER_CHANNELS);
var ORDER_CHANNEL_LABELS = { online: "Loja online", pdv: "PDV", manual: "Manual", whatsapp: "WhatsApp", instagram: "Instagram" };
var DELIVERY_METHODS = ["pickup", "ship"];
var orderDeliveryMethod = z9.enum(DELIVERY_METHODS);
var ORDER_DELIVERY_LABELS = { pickup: "Retirada na loja", ship: "Entrega" };
var PAYMENT_METHODS = ["pix", "credit_card", "debit_card", "boleto", "payment_link", "pay_on_pickup", "cash", "bank_transfer"];
var paymentMethod = z9.enum(PAYMENT_METHODS);
var PAYMENT_METHOD_LABELS = {
  pix: "PIX",
  credit_card: "Cart\xE3o de cr\xE9dito",
  debit_card: "Cart\xE3o de d\xE9bito",
  boleto: "Boleto",
  payment_link: "Link de pagamento",
  pay_on_pickup: "Pagar na retirada",
  cash: "Dinheiro",
  bank_transfer: "Transfer\xEAncia banc\xE1ria"
};
var PAYMENT_PROVIDER_METHODS = ["pix", "credit_card", "boleto", "payment_link"];
var PAYMENT_MANUAL_METHODS = ["pay_on_pickup", "cash", "bank_transfer"];
var PAYMENT_PICKUP_ONLY_METHODS = ["pay_on_pickup", "cash"];
var PAYMENT_PROVIDERS = ["asaas"];
var paymentProviderKey = z9.enum(PAYMENT_PROVIDERS);
var PAYMENT_STATUSES = ["pending", "confirmed", "failed", "expired", "canceled", "refunded", "partially_refunded", "chargeback"];
var PAYMENT_STATUS_LABELS = {
  pending: "Pendente",
  confirmed: "Confirmado",
  failed: "Falhou",
  expired: "Expirado",
  canceled: "Cancelado",
  refunded: "Reembolsado",
  partially_refunded: "Reembolso parcial",
  chargeback: "Chargeback"
};
var SHIPPING_KINDS = ["pickup", "flat", "zone", "provider"];
var SHIPPING_KIND_LABELS = { pickup: "Retirada na loja", flat: "Valor fixo", zone: "Por faixa de CEP", provider: "Transportadora (integra\xE7\xE3o)" };
var SHIPPING_PROVIDER_KEYS = ["correios", "melhor_envio"];
var cepDigits = z9.string().transform((v) => v.replace(/\D/g, "")).pipe(z9.string().regex(/^\d{8}$/, "CEP inv\xE1lido"));
var shippingZoneInput = z9.object({
  label: z9.string().trim().min(1).max(60),
  cepFrom: cepDigits,
  cepTo: cepDigits,
  priceCents: cents,
  etaDays: z9.number().int().min(0).max(90).nullish()
}).refine((z0) => z0.cepFrom <= z0.cepTo, { message: "CEP inicial maior que o final", path: ["cepTo"] });
var shippingMethodInput = z9.object({
  name: z9.string().trim().min(2).max(80),
  kind: z9.enum(SHIPPING_KINDS),
  active: z9.boolean().default(true),
  /** valor cobrado do cliente (kind flat) */
  priceCents: cents.default(0),
  /** frete grátis quando o valor das peças (já com desconto) alcança este valor */
  freeAboveCents: cents.nullish(),
  /** custo real do frete para a loja; a diferença para o valor cobrado vira "frete subsidiado" no financeiro */
  costCents: cents.nullish(),
  etaDays: z9.number().int().min(0).max(90).nullish(),
  zones: z9.array(shippingZoneInput).max(100).default([]),
  providerKey: z9.enum(SHIPPING_PROVIDER_KEYS).nullish(),
  instructions: optionalText(500),
  position: z9.number().int().min(0).max(999).default(0)
}).superRefine((v, ctx) => {
  if (v.kind === "zone" && v.zones.length === 0) ctx.addIssue({ code: "custom", path: ["zones"], message: "Informe ao menos uma faixa de CEP" });
  if (v.kind === "provider" && !v.providerKey) ctx.addIssue({ code: "custom", path: ["providerKey"], message: "Escolha a transportadora" });
});
var ORDER_NOTIFY_EVENTS = ["created", "paid", "separating", "ready_for_pickup", "shipped", "delivered", "canceled", "refunded"];
var ORDER_NOTIFY_LABELS = {
  created: "Pedido recebido",
  paid: "Pagamento confirmado",
  separating: "Em separa\xE7\xE3o",
  ready_for_pickup: "Pronto para retirada",
  shipped: "Enviado",
  delivered: "Entregue",
  canceled: "Cancelado",
  refunded: "Reembolsado"
};
var ORDER_TEMPLATE_VARIABLES = { cliente: "Nome do cliente", loja: "Nome da loja", pedido: "N\xFAmero do pedido", total: "Valor total", rastreio: "C\xF3digo de rastreio", link: "Link do pedido" };
var DEFAULT_ORDER_TEMPLATES = {
  created: { subject: "Recebemos o seu pedido {pedido}", body: "Ol\xE1, {cliente}! Recebemos o seu pedido {pedido} no valor de {total}. Acompanhe por aqui: {link}" },
  paid: { subject: "Pagamento confirmado: pedido {pedido}", body: "Ol\xE1, {cliente}! O pagamento do pedido {pedido} foi confirmado. J\xE1 estamos separando a sua pe\xE7a. {link}" },
  separating: { subject: "Pedido {pedido} em separa\xE7\xE3o", body: "Ol\xE1, {cliente}! Estamos separando o pedido {pedido}. {link}" },
  ready_for_pickup: { subject: "Pedido {pedido} pronto para retirada", body: "Ol\xE1, {cliente}! O pedido {pedido} est\xE1 pronto para retirada na loja {loja}. {link}" },
  shipped: { subject: "Pedido {pedido} enviado", body: "Ol\xE1, {cliente}! O pedido {pedido} foi enviado. C\xF3digo de rastreio: {rastreio}. {link}" },
  delivered: { subject: "Pedido {pedido} entregue", body: "Ol\xE1, {cliente}! O pedido {pedido} foi entregue. Obrigado por comprar na {loja}! {link}" },
  canceled: { subject: "Pedido {pedido} cancelado", body: "Ol\xE1, {cliente}. O pedido {pedido} foi cancelado e a pe\xE7a voltou \xE0 loja. {link}" },
  refunded: { subject: "Reembolso do pedido {pedido}", body: "Ol\xE1, {cliente}. O pedido {pedido} foi reembolsado. O valor volta pelo mesmo meio de pagamento. {link}" }
};
var VAR_RE = /\{(\w+)\}/g;
function renderOrderTemplate(template, values) {
  return template.replace(VAR_RE, (whole, name) => {
    if (!(name in ORDER_TEMPLATE_VARIABLES)) return whole;
    const v = values[name];
    return v == null ? "" : String(v).replace(/[\u0000-\u001f\u007f]+/g, " ").trim().slice(0, 200);
  }).replace(/\s{2,}/g, " ").trim();
}
var tplText = (max) => z9.string().trim().max(max);
var orderTemplatesInput = z9.partialRecord(z9.enum(ORDER_NOTIFY_EVENTS), z9.object({ subject: tplText(150).min(1), body: tplText(1e3).min(1) })).default({});
var orderSettingsInput = z9.object({
  /** reserva estendida para PIX (minutos); vazio = usa o prazo padrão do estoque */
  pixTtlMinutes: z9.number().int().min(5).max(24 * 60).nullish(),
  /** quanto tempo a peça fica reservada aguardando pagamento presencial/manual (horas) */
  manualTtlHours: z9.number().int().min(1).max(24 * 14).default(48),
  allowGuestCheckout: z9.boolean().default(true),
  allowPickup: z9.boolean().default(true),
  pickupInstructions: optionalText(500),
  notifyEmail: z9.boolean().default(true),
  notifyWhatsapp: z9.boolean().default(true),
  templates: orderTemplatesInput
});
var checkoutAddressInput = addressSchema.extend({ recipient: z9.string().trim().min(2).max(120).optional() });
var checkoutGuestInput = z9.object({
  name: z9.string().trim().min(2).max(120),
  email: emailSchema,
  phone: phoneBR
});
var productIds = z9.array(uuid).min(1, "O carrinho est\xE1 vazio").max(30, "M\xE1ximo de 30 pe\xE7as por pedido").refine((ids) => new Set(ids).size === ids.length, "Pe\xE7a repetida no carrinho");
var couponCode = z9.string().trim().max(40).optional().transform((v) => v ? v : void 0);
var checkoutQuoteInput = z9.object({
  productIds,
  couponCode,
  redeemPoints: z9.number().int().min(0).max(1e7).optional(),
  /** usa o crédito da loja do cliente logado como forma de pagamento (até o valor das peças) */
  useStoreCredit: z9.boolean().optional(),
  delivery: z9.object({ method: orderDeliveryMethod, shippingMethodId: uuid.optional(), cep: cepDigits.optional() }).optional()
});
var checkoutOrderInput = z9.object({
  productIds,
  couponCode,
  redeemPoints: z9.number().int().min(0).max(1e7).optional(),
  useStoreCredit: z9.boolean().optional(),
  delivery: z9.discriminatedUnion("method", [
    z9.object({ method: z9.literal("pickup"), shippingMethodId: uuid.optional() }),
    z9.object({ method: z9.literal("ship"), shippingMethodId: uuid, address: checkoutAddressInput })
  ]),
  paymentMethod,
  /** CPF do pagador: o provedor exige para PIX, cartão e boleto (opcional nos meios presenciais) */
  payerCpf: cpfBR.optional(),
  /** obrigatório para visitante (sem sessão); ignorado para cliente logado */
  guest: checkoutGuestInput.optional(),
  note: optionalText(300)
}).strict();
var checkoutRetryPaymentInput = z9.object({ paymentMethod, payerCpf: cpfBR.optional() });
var csv = (values) => z9.string().max(300).transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean)).pipe(z9.array(z9.enum(values)));
var orderListQuery = paginationQuery.extend({
  status: csv(ORDER_STATUSES).optional(),
  channel: csv(ORDER_CHANNELS).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  customerId: uuid.optional(),
  paymentMethod: csv(PAYMENT_METHODS).optional()
});
var reasonText = (min) => z9.string().trim().min(min, `Informe a justificativa (m\xEDn. ${min} caracteres)`).max(500);
var orderStatusInput = z9.object({
  to: orderStatus,
  reason: optionalText(500),
  trackingCode: z9.string().trim().min(3).max(60).optional(),
  carrier: z9.string().trim().max(60).optional()
});
var orderCancelInput = z9.object({ reason: reasonText(5) });
var orderConfirmPaymentInput = z9.object({
  method: z9.enum(["pay_on_pickup", "cash", "bank_transfer"]),
  reason: reasonText(5),
  paymentId: uuid.optional()
});
var paymentRefundInput = z9.object({
  /** vazio = reembolso total do saldo */
  amountCents: cents.refine((v) => v > 0, "Informe um valor maior que zero").optional(),
  reason: reasonText(5),
  /** a peça nunca saiu da loja (pedido ainda não enviado/entregue): volta ao estoque na hora */
  restock: z9.boolean().default(false)
});
var PAYMENT_ENVIRONMENTS = ["sandbox", "production"];
var paymentCredentialsInput = z9.object({
  provider: paymentProviderKey,
  environment: z9.enum(PAYMENT_ENVIRONMENTS),
  /** nunca devolvida depois de salva */
  apiKey: z9.string().trim().min(10).max(300)
});
var paymentSettingsInput = z9.object({
  enabledMethods: z9.array(paymentMethod).max(PAYMENT_METHODS.length),
  bankInstructions: optionalText(600),
  pickupPaymentInstructions: optionalText(600),
  providerAccountId: uuid.nullish(),
  manualAccountId: uuid.nullish()
});

// src/dashboard.ts
var day = isoDate.refine((v) => !Number.isNaN(Date.parse(v)), "Data inv\xE1lida");
var within366 = (v) => !v.from || !v.to || (Date.parse(v.to) - Date.parse(v.from)) / 864e5 <= 366;
var ordered = (v) => !v.from || !v.to || v.from <= v.to;
var dashboardQuery = z10.object({ from: day.optional(), to: day.optional(), branchId: uuid.optional(), channel: orderChannel.optional(), categoryId: uuid.optional() }).refine(ordered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(within366, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var periodReportQuery = z10.object({ from: day, to: day, branchId: uuid.optional() }).refine(ordered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(within366, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var auditQuery = paginationQuery.extend({
  action: z10.string().trim().max(80).optional(),
  entityType: z10.string().trim().max(60).optional(),
  entityId: uuid.optional(),
  userId: uuid.optional(),
  from: day.optional(),
  to: day.optional()
});
var recordAccessQuery = paginationQuery.extend({
  customerId: uuid.optional(),
  userId: uuid.optional(),
  from: day.optional(),
  to: day.optional()
});
var RECORD_ACCESS_ACTIONS = ["customer.viewed", "customer.exported", "supplier.viewed", "supplier.exported", "consignor.viewed", "consignor.payment_viewed", "consignor.exported", "order.viewed", "order.exported"];

// src/platform.ts
import { z as z11 } from "zod";
var PLATFORM_ROLES = ["owner", "support", "finance", "readonly"];
var PLATFORM_ROLE_LABEL = {
  owner: "Propriet\xE1rio",
  support: "Suporte",
  finance: "Financeiro",
  readonly: "Somente leitura"
};
var LIMIT_KEYS = ["users", "units", "orders_month", "storage_mb", "ai_requests_month", "active_products", "images"];
var LIMIT_LABEL = {
  users: "Usu\xE1rios",
  units: "Unidades",
  orders_month: "Pedidos por m\xEAs",
  storage_mb: "Armazenamento (MB)",
  ai_requests_month: "Consultas de IA por m\xEAs",
  active_products: "Pe\xE7as ativas",
  images: "Fotos de pe\xE7as"
};
var PLAN_INTERVALS = ["month", "year"];
var SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "grace", "suspended", "canceled"];
var SUBSCRIPTION_STATUS_LABEL = {
  trialing: "Em teste",
  active: "Ativa",
  past_due: "Pagamento em atraso",
  grace: "Em car\xEAncia",
  suspended: "Suspensa",
  canceled: "Cancelada"
};
var SUBSCRIPTION_ACTIONS = ["upgrade", "downgrade", "cancel", "reactivate", "trial_start", "trial_convert", "trial_extend", "status_change"];
var ORG_STATUSES = ["active", "suspended", "pending_deletion"];
var ORG_STATUS_LABEL = {
  active: "Ativa",
  suspended: "Suspensa",
  pending_deletion: "Exclus\xE3o agendada"
};
var limitValue = z11.number().int().min(0).max(1e9).nullable();
var planLimitsInput = z11.object({
  users: limitValue,
  units: limitValue,
  orders_month: limitValue,
  storage_mb: limitValue,
  ai_requests_month: limitValue,
  /** peças que ainda não foram vendidas, arquivadas nem retiradas */
  active_products: limitValue.default(null),
  /** total de fotos de peças da loja */
  images: limitValue.default(null)
});
var platformPlanInput = z11.object({
  key: z11.string().trim().min(2, "M\xEDnimo de 2 caracteres").max(40).regex(/^[a-z0-9_-]+$/, "Use letras min\xFAsculas, n\xFAmeros, h\xEDfen ou sublinhado"),
  name: z11.string().trim().min(2, "Informe o nome").max(80),
  description: z11.string().trim().max(300).default(""),
  active: z11.boolean().default(true),
  public: z11.boolean().default(true),
  priceCents: cents,
  currency: z11.literal("BRL").default("BRL"),
  interval: z11.enum(PLAN_INTERVALS).default("month"),
  trialDays: z11.number().int().min(0).max(365).default(14),
  sort: z11.number().int().min(0).max(1e3).default(0),
  limits: planLimitsInput,
  /** chave do recurso -> habilitado */
  features: z11.record(z11.string(), z11.boolean()).default({})
});
var featureFlagInput = z11.object({
  key: z11.string().trim().min(2).max(60).regex(/^[a-z0-9_.-]+$/, "Use letras min\xFAsculas, n\xFAmeros, ponto, h\xEDfen ou sublinhado"),
  default: z11.boolean().default(false),
  description: z11.string().trim().max(200).default("")
});
var orgFeatureOverrideInput = z11.object({ enabled: z11.boolean().nullable() });
var platformOrgListQuery = paginationQuery.extend({
  status: z11.enum(ORG_STATUSES).optional(),
  subscriptionStatus: z11.enum(SUBSCRIPTION_STATUSES).optional(),
  planId: z11.uuid().optional(),
  createdFrom: isoDate.optional(),
  createdTo: isoDate.optional()
});
var PLATFORM_ORG_SORTS = ["name", "createdAt", "status", "plan", "subscriptionStatus"];
var PLATFORM_CREDENTIALS = ["password", "link"];
var credentialFields = {
  credential: z11.enum(PLATFORM_CREDENTIALS, { error: "Escolha como o usu\xE1rio vai acessar" }),
  password: z11.string().max(128).optional()
};
function requirePasswordWhenChosen(v, ctx) {
  if (v.credential !== "password") return;
  const r = passwordSchema.safeParse(v.password ?? "");
  if (!r.success) ctx.addIssue({ code: "custom", path: ["password"], message: r.error.issues[0]?.message ?? "Senha inv\xE1lida" });
}
var platformOrgCreateInput = z11.object({
  name: z11.string().trim().min(2).max(120),
  timezone: z11.string().default("America/Sao_Paulo"),
  planId: z11.uuid().optional(),
  /** dias de teste (0 a 365); sem valor, usa o padrão do plano */
  trialDays: z11.number().int().min(0).max(365).optional(),
  owner: z11.object({
    name: z11.string().trim().min(2, "Informe o nome").max(120),
    email: z11.email("E-mail inv\xE1lido").trim().toLowerCase().max(254),
    ...credentialFields
  }).superRefine(requirePasswordWhenChosen)
});
var platformOrgUserInput = z11.object({
  name: z11.string().trim().min(2, "Informe o nome").max(120),
  email: z11.email("E-mail inv\xE1lido").trim().toLowerCase().max(254),
  roleKey: z11.string().trim().min(1, "Escolha o papel").max(30),
  ...credentialFields
}).superRefine(requirePasswordWhenChosen);
var platformMemberUpdateInput = z11.object({ roleKey: z11.string().trim().min(1).max(30).optional(), active: z11.boolean().optional() }).refine((v) => v.roleKey !== void 0 || v.active !== void 0, { message: "Nada para alterar" });
var suspendOrgInput = z11.object({ reason: z11.string().trim().min(3, "Informe o motivo").max(300) });
var scheduleDeletionInput = z11.object({ reason: z11.string().trim().min(3, "Informe o motivo").max(300) });
var confirmDeletionInput = z11.object({ confirmName: z11.string().trim().min(1) });
var changePlanInput = z11.object({ planId: z11.uuid() });
var extendTrialInput = z11.object({ days: z11.number().int().min(1).max(365) });
var cancelSubscriptionInput = z11.object({ immediately: z11.boolean().default(false) });
var featureOverrideInput = orgFeatureOverrideInput;
var ONBOARDING_STEPS = ["store", "product", "publish", "payment", "shipping", "storefront"];
var onboardingStateInput = z11.object({
  wizardCompleted: z11.boolean().optional(),
  dismissed: z11.boolean().optional(),
  skippedSteps: z11.array(z11.enum(ONBOARDING_STEPS)).optional()
});
var BILLING_EVENT_STATUSES = ["pending", "processing", "processed", "failed", "ignored"];
var billingEventsQuery = paginationQuery.extend({
  status: z11.enum(BILLING_EVENT_STATUSES).optional(),
  eventType: z11.string().max(60).optional()
});
var PLATFORM_INVOICE_STATUSES = ["pending", "confirmed", "received", "overdue", "refunded", "canceled"];
var platformInvoicesQuery = paginationQuery.extend({
  status: z11.enum(PLATFORM_INVOICE_STATUSES).optional()
});
var SUSPEND_POLICIES = ["auto", "manual"];
var platformSettingsInput = z11.object({
  /** dias em carência (após o período em atraso) antes de suspender */
  graceDays: z11.number().int().min(0).max(90),
  /** dias em atraso (past_due) antes de entrar em carência */
  pastDueDays: z11.number().int().min(0).max(60),
  suspendPolicy: z11.enum(SUSPEND_POLICIES),
  /** dias de retenção dos dados após cancelamento/suspensão antes de permitir exclusão */
  retentionDays: z11.number().int().min(0).max(3650),
  /** incluir assinaturas em atraso (past_due) no MRR */
  mrrIncludesPastDue: z11.boolean(),
  /** custo estimado de IA em micro-reais por 1.000 tokens */
  aiCostMicrosPer1kTokens: z11.number().int().min(0).max(1e8),
  /** teto mensal de custo de IA por loja, em micro-reais (null = sem teto) */
  aiMonthlyCostCapMicros: z11.number().int().min(0).max(1e11).nullable().default(null),
  /** percentual do teto que dispara o alerta */
  aiCostAlertPercent: z11.number().int().min(1).max(100).default(80)
});
var platformUsageQuery = paginationQuery.extend({
  period: z11.string().regex(/^\d{4}-\d{2}$/).optional()
});
var platformMetricsQuery = z11.object({
  from: isoDate.optional(),
  to: isoDate.optional()
});
var TICKET_STATUSES = ["open", "pending", "resolved", "closed"];
var TICKET_PRIORITIES = ["low", "normal", "high", "urgent"];
var TICKET_STATUS_LABEL = { open: "Aberto", pending: "Aguardando", resolved: "Resolvido", closed: "Fechado" };
var TICKET_PRIORITY_LABEL = { low: "Baixa", normal: "Normal", high: "Alta", urgent: "Urgente" };
var ticketListQuery = paginationQuery.extend({
  status: z11.enum(TICKET_STATUSES).optional(),
  priority: z11.enum(TICKET_PRIORITIES).optional()
});
var ticketCreateInput = z11.object({
  subject: z11.string().trim().min(3, "Informe o assunto").max(160),
  message: z11.string().trim().min(3, "Descreva o problema").max(4e3),
  priority: z11.enum(TICKET_PRIORITIES).default("normal")
});
var ticketMessageInput = z11.object({ text: z11.string().trim().min(1, "Escreva a mensagem").max(4e3) });
var ticketUpdateInput = z11.object({
  status: z11.enum(TICKET_STATUSES).optional(),
  priority: z11.enum(TICKET_PRIORITIES).optional()
});
var platformAuditQuery = paginationQuery.extend({
  action: z11.string().max(80).optional(),
  organizationId: z11.uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});
var platformAdminInput = z11.object({
  email: z11.email("E-mail inv\xE1lido").trim().toLowerCase().max(254),
  role: z11.enum(PLATFORM_ROLES)
});
var platformAdminUpdateInput = z11.object({ role: z11.enum(PLATFORM_ROLES) });
var PLAN_LIMIT_ERROR_CODE = "plan_limit_reached";
var FEATURE_UNAVAILABLE_ERROR_CODE = "feature_unavailable";
var ORG_SUSPENDED_ERROR_CODE = "organization_suspended";
var platformSubscriptionListQuery = paginationQuery.extend({
  status: z11.enum(SUBSCRIPTION_STATUSES).optional(),
  planId: z11.uuid().optional()
});
var PLATFORM_SUBSCRIPTION_SORTS = ["organization", "plan", "status", "trialEndsAt", "currentPeriodEnd", "price"];
var IMPERSONATION_TTL_MINUTES = 30;
var impersonateInput = z11.object({
  userId: z11.uuid(),
  /** Opcional; string vazia vira ausente. */
  reason: z11.string().trim().max(300, "M\xE1ximo de 300 caracteres").optional().transform((v) => v ? v : void 0)
});
var PLATFORM_USER_STATUSES = ["active", "inactive"];
var PLATFORM_USER_STATUS_LABEL = { active: "Ativo", inactive: "Inativo" };
var platformUsersQuery = paginationQuery.extend({
  organizationId: z11.uuid().optional(),
  /** chave do papel do tenant (owner, manager, seller...) */
  role: z11.string().trim().min(1).max(30).optional(),
  status: z11.enum(PLATFORM_USER_STATUSES).optional()
});
var PLATFORM_USER_SORTS = ["name", "email", "createdAt"];
var REMOVED_EMAIL_DOMAIN = "removido.invalid";
var manageReason = z11.string().trim().min(10, "Descreva o motivo com ao menos 10 caracteres").max(300, "M\xE1ximo de 300 caracteres");
var platformUserUpdateInput = z11.object({
  name: z11.string().trim().min(2, "Informe o nome").max(120).optional(),
  email: emailSchema.optional(),
  active: z11.boolean().optional(),
  organizationId: z11.uuid().optional()
}).refine((v) => v.active === void 0 || !!v.organizationId, { path: ["organizationId"], message: "Informe a organiza\xE7\xE3o" }).refine((v) => v.name !== void 0 || v.email !== void 0 || v.active !== void 0, { message: "Nada para alterar" });
var platformUserSetPasswordInput = z11.object({ password: passwordSchema, reason: manageReason });
var platformUserDeleteInput = z11.object({ reason: manageReason });

// src/legal.ts
var LEGAL_VERSION = "2026-10-22";

// src/whatsapp.ts
import { z as z12 } from "zod";
var WHATSAPP_VARIABLES = {
  cliente: "Nome do cliente",
  pedido: "N\xFAmero do pedido ou da reserva",
  data: "Data (dd/mm/aaaa)",
  hora: "Hor\xE1rio (hh:mm)",
  loja: "Nome da loja"
};
var WHATSAPP_VARIABLE_KEYS = Object.keys(WHATSAPP_VARIABLES);
var WHATSAPP_TEMPLATE_KINDS = ["confirmation", "reminder", "cancellation"];
var WHATSAPP_TEMPLATE_LABEL = {
  confirmation: "Confirma\xE7\xE3o de pedido",
  reminder: "Lembrete de reserva",
  cancellation: "Cancelamento"
};
var DEFAULT_WHATSAPP_TEMPLATES = {
  confirmation: "Ol\xE1, {{cliente}}! Recebemos o seu pedido {{pedido}} na {{loja}}. Obrigado pela compra!",
  reminder: "Ol\xE1, {{cliente}}! Sua reserva {{pedido}} na {{loja}} vence em {{data}} \xE0s {{hora}}. Se ainda quiser a pe\xE7a, finalize antes do prazo.",
  cancellation: "Ol\xE1, {{cliente}}. O pedido {{pedido}} na {{loja}} foi cancelado. Se tiver d\xFAvidas, \xE9 s\xF3 responder esta mensagem."
};
var WHATSAPP_TEMPLATE_MAX = 600;
var WHATSAPP_REMINDER_HOURS = [2, 4, 12, 24, 48];
var VAR_RE2 = /\{\{\s*([a-zA-Z_]+)\s*\}\}/g;
function unknownWhatsappVariables(text5) {
  const out = /* @__PURE__ */ new Set();
  for (const m of text5.matchAll(VAR_RE2)) {
    if (!(m[1] in WHATSAPP_VARIABLES)) out.add(m[1]);
  }
  return [...out];
}
var clean = (v) => v.replace(/[\u0000-\u001f\u007f]+/g, " ").trim().slice(0, 160);
function renderWhatsappTemplate(template, values) {
  return template.replace(VAR_RE2, (whole, name) => {
    if (!(name in WHATSAPP_VARIABLES)) return whole;
    const v = values[name];
    return v == null ? "" : clean(String(v));
  }).replace(/\n{3,}/g, "\n\n").trim();
}
var templateField = z12.string().max(WHATSAPP_TEMPLATE_MAX, `M\xE1ximo de ${WHATSAPP_TEMPLATE_MAX} caracteres`).refine((v) => unknownWhatsappVariables(v).length === 0, {
  error: (iss) => `Vari\xE1vel n\xE3o permitida: ${unknownWhatsappVariables(String(iss.input)).map((x) => `{{${x}}}`).join(", ")}`
}).transform((v) => v.trim() === "" ? null : v).nullable();
var whatsappSettingsInput = z12.object({
  enabled: z12.boolean().optional(),
  sendConfirmation: z12.boolean().optional(),
  sendReminder: z12.boolean().optional(),
  sendCancellation: z12.boolean().optional(),
  reminderHours: z12.number().int().refine((v) => WHATSAPP_REMINDER_HOURS.includes(v), "Anteced\xEAncia inv\xE1lida").optional(),
  tplConfirmation: templateField.optional(),
  tplReminder: templateField.optional(),
  tplCancellation: templateField.optional()
});
var whatsappTestInput = z12.object({
  phone: z12.string().transform((v) => v.replace(/\D/g, "")).pipe(z12.string().regex(/^\d{10,11}$/, "Telefone inv\xE1lido"))
});
var WHATSAPP_STATUSES = ["disconnected", "connecting", "connected"];
var WHATSAPP_UNAVAILABLE_MESSAGE = "O WhatsApp n\xE3o est\xE1 dispon\xEDvel no momento. Fale com o suporte.";

// src/marketing.ts
import { z as z13 } from "zod";
var boolQuery2 = z13.enum(["true", "false"]).transform((v) => v === "true");
var SALES_CHANNELS = ["online", "pos"];
var salesChannel = z13.enum(SALES_CHANNELS);
var SALES_CHANNEL_LABEL = { online: "Loja online", pos: "PDV (loja f\xEDsica)" };
var DISCOUNT_TYPES = ["percent", "fixed"];
var discountType = z13.enum(DISCOUNT_TYPES);
var MARKETING_SCOPES = ["store", "categories", "brands", "products"];
var marketingScope = z13.enum(MARKETING_SCOPES);
var MARKETING_SCOPE_LABEL = {
  store: "Loja toda",
  categories: "Categorias",
  brands: "Marcas",
  products: "Pe\xE7as selecionadas"
};
var bps = z13.number().int().min(1).max(1e4);
var uuidList = z13.array(z13.uuid()).max(500);
var discountShape = {
  discountType,
  /** percentual em pontos-base (1500 = 15%); obrigatório se `discountType = percent` */
  percentBps: bps.optional().nullable(),
  /** valor fixo em centavos; obrigatório se `discountType = fixed` */
  amountCents: cents.min(1).optional().nullable(),
  scope: marketingScope.default("store"),
  /** ids de categorias, marcas ou peças conforme o escopo (vazio com escopo `store`) */
  scopeIds: uuidList.default([])
};
function refineDiscount(v, ctx) {
  if (v.discountType === "percent" && !v.percentBps) ctx.addIssue({ code: "custom", path: ["percentBps"], message: "Informe o percentual de desconto" });
  if (v.discountType === "fixed" && !v.amountCents) ctx.addIssue({ code: "custom", path: ["amountCents"], message: "Informe o valor do desconto" });
  if (v.scope !== "store" && v.scopeIds.length === 0) ctx.addIssue({ code: "custom", path: ["scopeIds"], message: "Selecione ao menos um item do escopo" });
}
var couponCode2 = z13.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9_-]{2,31}$/, "C\xF3digo com 3 a 32 caracteres: letras, n\xFAmeros, h\xEDfen ou sublinhado");
var COUPON_STATUSES = ["active", "paused"];
var couponStatus = z13.enum(COUPON_STATUSES);
var couponInput = z13.object({
  code: couponCode2,
  name: z13.string().trim().min(2).max(120),
  description: optionalText(500),
  ...discountShape,
  /** teto do desconto (útil em cupons percentuais) */
  maxDiscountCents: cents.min(1).optional().nullable(),
  startsAt: isoDateTime.optional().nullable(),
  endsAt: isoDateTime.optional().nullable(),
  /** limite total de usos; vazio = ilimitado */
  usageLimit: z13.number().int().min(1).max(1e6).optional().nullable(),
  /** limite por cliente; vazio = ilimitado */
  perCustomerLimit: z13.number().int().min(1).max(1e3).optional().nullable(),
  minOrderCents: cents.default(0),
  firstPurchaseOnly: z13.boolean().default(false),
  channels: z13.array(salesChannel).min(1, "Selecione ao menos um canal").default(["online", "pos"]),
  status: couponStatus.default("active")
}).superRefine((v, ctx) => {
  refineDiscount(v, ctx);
  if (v.startsAt && v.endsAt && new Date(v.endsAt) <= new Date(v.startsAt)) ctx.addIssue({ code: "custom", path: ["endsAt"], message: "O fim precisa ser depois do in\xEDcio" });
  if (v.discountType === "percent" && v.percentBps && v.percentBps > 1e4) ctx.addIssue({ code: "custom", path: ["percentBps"], message: "Percentual m\xE1ximo \xE9 100%" });
});
var COUPON_VIEW_STATUSES = ["active", "paused", "scheduled", "expired", "exhausted"];
var couponFilters = z13.object({
  q: z13.string().trim().max(120).optional(),
  status: z13.enum(COUPON_VIEW_STATUSES).optional(),
  discountType: discountType.optional(),
  scope: marketingScope.optional()
});
var couponListQuery = paginationQuery.extend(couponFilters.shape);
var couponExportQuery = couponFilters;
var PROMOTION_STATUSES = ["active", "paused"];
var promotionStatus = z13.enum(PROMOTION_STATUSES);
var promotionInput = z13.object({
  name: z13.string().trim().min(2).max(120),
  description: optionalText(500),
  ...discountShape,
  startsAt: isoDateTime,
  endsAt: isoDateTime,
  /** maior prioridade vence quando mais de uma promoção alcança a mesma peça (nunca acumulam entre si) */
  priority: z13.number().int().min(0).max(1e3).default(0),
  /** pode somar com cupom nas peças que ela alcança */
  combinableWithCoupon: z13.boolean().default(false),
  channels: z13.array(salesChannel).min(1).default(["online", "pos"]),
  status: promotionStatus.default("active")
}).superRefine((v, ctx) => {
  refineDiscount(v, ctx);
  if (new Date(v.endsAt) <= new Date(v.startsAt)) ctx.addIssue({ code: "custom", path: ["endsAt"], message: "O fim precisa ser depois do in\xEDcio" });
});
var PROMOTION_VIEW_STATUSES = ["running", "scheduled", "ended", "paused"];
var promotionFilters = z13.object({
  q: z13.string().trim().max(120).optional(),
  status: z13.enum(PROMOTION_VIEW_STATUSES).optional(),
  scope: marketingScope.optional()
});
var promotionListQuery = paginationQuery.extend(promotionFilters.shape);
var promotionExportQuery = promotionFilters;
var promoCampaignInput = z13.object({
  name: z13.string().trim().min(2).max(120),
  /** texto de divulgação (vitrine, redes) */
  text: optionalText(2e3),
  startsAt: isoDateTime,
  endsAt: isoDateTime,
  productIds: uuidList.default([]),
  /** promoção aplicada às peças da campanha (opcional) */
  promotionId: z13.uuid().optional().nullable(),
  status: promotionStatus.default("active")
}).superRefine((v, ctx) => {
  if (new Date(v.endsAt) <= new Date(v.startsAt)) ctx.addIssue({ code: "custom", path: ["endsAt"], message: "O fim precisa ser depois do in\xEDcio" });
});
var promoCampaignListQuery = paginationQuery.extend({
  q: z13.string().trim().max(120).optional(),
  status: z13.enum(PROMOTION_VIEW_STATUSES).optional()
});
var loyaltyTier = z13.object({
  name: z13.string().trim().min(1).max(40),
  /** pontos acumulados (vitalício) para entrar no nível */
  minLifetimePoints: z13.number().int().min(0).max(1e8),
  /** multiplicador de pontos em % (100 = 1x, 150 = 1,5x) */
  pointsMultiplierPct: z13.number().int().min(100).max(1e3).default(100),
  /** desconto automático em % (pontos-base) para quem está no nível; 0 = sem */
  benefitDiscountBps: z13.number().int().min(0).max(5e3).default(0)
});
var marketingSettingsInput = z13.object({
  /** nunca vender abaixo do custo da peça com desconto de promoção/cupom */
  enforceCostFloor: z13.boolean().default(false),
  loyaltyEnabled: z13.boolean().default(false),
  /** pontos ganhos por R$ 1,00 pago (inteiro) */
  pointsPerReal: z13.number().int().min(0).max(1e3).default(1),
  /** valor de 1 ponto em centavos no resgate */
  pointValueCents: z13.number().int().min(0).max(1e4).default(1),
  /** dias até os pontos expirarem; vazio = nunca */
  pointsExpiryDays: z13.number().int().min(1).max(3650).optional().nullable(),
  /** mínimo de pontos por resgate */
  minRedeemPoints: z13.number().int().min(0).max(1e7).default(0),
  tiers: z13.array(loyaltyTier).max(6).default([]),
  firstPurchaseEnabled: z13.boolean().default(false),
  /** cupom (já cadastrado, com "primeira compra") aplicado automaticamente ao cliente novo */
  firstPurchaseCouponId: z13.uuid().optional().nullable(),
  /** limite de mensagens de campanha por canal por dia */
  dailySendLimit: z13.number().int().min(1).max(1e5).default(200)
}).superRefine((v, ctx) => {
  if (v.firstPurchaseEnabled && !v.firstPurchaseCouponId) ctx.addIssue({ code: "custom", path: ["firstPurchaseCouponId"], message: "Escolha o cupom de primeira compra" });
  const mins = v.tiers.map((t) => t.minLifetimePoints);
  if (new Set(mins).size !== mins.length) ctx.addIssue({ code: "custom", path: ["tiers"], message: "Dois n\xEDveis n\xE3o podem ter a mesma pontua\xE7\xE3o m\xEDnima" });
});
var LOYALTY_ENTRY_KINDS = ["earn", "redeem", "expire", "earn_reversal", "redeem_reversal", "adjust"];
var LOYALTY_ENTRY_LABEL = {
  earn: "Ac\xFAmulo",
  redeem: "Resgate",
  expire: "Expira\xE7\xE3o",
  earn_reversal: "Estorno de ac\xFAmulo",
  redeem_reversal: "Estorno de resgate",
  adjust: "Ajuste manual"
};
var loyaltyLedgerQuery = paginationQuery.extend({ kind: z13.enum(LOYALTY_ENTRY_KINDS).optional() });
var loyaltyAdjustInput = z13.object({
  /** positivo credita, negativo debita (nunca deixa o saldo negativo) */
  points: z13.number().int().min(-1e6).max(1e6).refine((n) => n !== 0, "Informe uma quantidade diferente de zero"),
  reason: z13.string().trim().min(3, "Informe o motivo").max(200)
});
var customerPreferencesInput = z13.object({
  sizes: z13.array(z13.string().trim().min(1).max(12)).max(20).default([]),
  styles: z13.array(z13.string().trim().min(1).max(40)).max(20).default([])
});
var segmentRules = z13.object({
  /** frequência: nº de pedidos pagos */
  minOrders: z13.number().int().min(0).max(1e5).optional().nullable(),
  maxOrders: z13.number().int().min(0).max(1e5).optional().nullable(),
  /** valor gasto (centavos) */
  minSpentCents: cents.optional().nullable(),
  maxSpentCents: cents.optional().nullable(),
  /** recência: comprou nos últimos N dias */
  boughtWithinDays: z13.number().int().min(1).max(3650).optional().nullable(),
  /** recência: sem comprar há pelo menos N dias (clientes sem nenhuma compra entram) */
  inactiveForDays: z13.number().int().min(1).max(3650).optional().nullable(),
  /** preferência declarada (qualquer um dos tamanhos / estilos) */
  sizes: z13.array(z13.string().trim().min(1).max(12)).max(20).default([]),
  styles: z13.array(z13.string().trim().min(1).max(40)).max(20).default([]),
  /** só quem aceitou receber por este canal */
  consentChannel: z13.enum(["whatsapp", "email"]).optional().nullable()
}).superRefine((v, ctx) => {
  if (v.minOrders != null && v.maxOrders != null && v.minOrders > v.maxOrders) ctx.addIssue({ code: "custom", path: ["maxOrders"], message: "M\xE1ximo menor que o m\xEDnimo" });
  if (v.minSpentCents != null && v.maxSpentCents != null && v.minSpentCents > v.maxSpentCents) ctx.addIssue({ code: "custom", path: ["maxSpentCents"], message: "M\xE1ximo menor que o m\xEDnimo" });
});
var segmentInput = z13.object({
  name: z13.string().trim().min(2).max(120),
  description: optionalText(500),
  rules: segmentRules
});
var segmentListQuery = paginationQuery;
var segmentMembersQuery = paginationQuery;
var COMM_CHANNELS = ["whatsapp", "email"];
var commChannel = z13.enum(COMM_CHANNELS);
var COMM_STATUSES = ["draft", "scheduled", "sending", "sent", "unavailable", "cancelled"];
var COMM_STATUS_LABEL = {
  draft: "Rascunho",
  scheduled: "Agendada",
  sending: "Enviando",
  sent: "Conclu\xEDda",
  unavailable: "Indispon\xEDvel",
  cancelled: "Cancelada"
};
var COMM_VARIABLES = ["cliente", "loja", "link_descadastro"];
var commCampaignInput = z13.object({
  name: z13.string().trim().min(2).max(120),
  channel: commChannel,
  /** vazio = todos os clientes com consentimento */
  segmentId: z13.uuid().optional().nullable(),
  subject: optionalText(150),
  body: z13.string().trim().min(5, "Escreva a mensagem").max(4e3)
});
var commScheduleInput = z13.object({
  /** vazio = enviar agora */
  scheduledAt: isoDateTime.optional().nullable()
});
var commCampaignListQuery = paginationQuery.extend({
  status: z13.enum(COMM_STATUSES).optional(),
  channel: commChannel.optional()
});
var DELIVERY_STATUSES = [
  "queued",
  "sent",
  "failed",
  "skipped_consent",
  "skipped_invalid",
  "skipped_limit",
  "skipped_optout",
  "skipped_unconfigured",
  "skipped_disabled",
  "skipped_invalid_phone"
];
var deliveryListQuery = paginationQuery.extend({ status: z13.enum(DELIVERY_STATUSES).optional() });
var deliveryExportQuery = z13.object({ status: z13.enum(DELIVERY_STATUSES).optional() });
var unsubscribeListQuery = paginationQuery.extend({ channel: commChannel.optional() });
var unsubscribeInput = z13.object({ token: z13.string().min(20).max(500) });
var pricingItem = z13.object({
  productId: z13.uuid(),
  categoryId: z13.uuid().nullable().optional(),
  brandId: z13.uuid().nullable().optional(),
  priceCents: cents,
  /** custo da peça; usado só quando a loja ativou o piso (nunca abaixo do custo) */
  costCents: cents.nullable().optional()
});
var pricingQuoteInput = z13.object({
  items: z13.array(pricingItem).min(1).max(200),
  customerId: z13.uuid().nullable().optional(),
  channel: salesChannel,
  couponCode: z13.string().trim().max(40).nullable().optional(),
  /** pontos que o cliente quer resgatar (simulação; o resgate real é feito pelo pedido) */
  redeemPoints: z13.number().int().min(0).max(1e8).optional()
});

// src/finance.ts
import { z as z14 } from "zod";
var FINANCE_CHANNELS = ["online_store", "pos", "instagram", "whatsapp", "marketplace", "consignment", "other"];
var financeChannel = z14.enum(FINANCE_CHANNELS);
var CHANNEL_LABELS = {
  online_store: "Loja online",
  pos: "PDV (loja f\xEDsica)",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  marketplace: "Marketplace",
  consignment: "Consigna\xE7\xE3o",
  other: "Outro canal"
};
var categoryKind = z14.enum(["income", "expense"]);
var CATEGORY_KIND_LABELS = { income: "Receita", expense: "Despesa" };
var CATEGORY_ROLES = ["sale", "other_income", "refund", "payment_fee", "shipping_subsidy", "consignment_payout", "inventory", "operating"];
var categoryRole = z14.enum(CATEGORY_ROLES);
var expenseNature = z14.enum(["fixed", "variable"]);
var NATURE_LABELS = { fixed: "Fixa", variable: "Vari\xE1vel" };
var accountType = z14.enum(["cash", "bank", "wallet"]);
var ACCOUNT_TYPE_LABELS = { cash: "Caixa", bank: "Banco", wallet: "Carteira do provedor de pagamento" };
var entryKind = z14.enum(["income", "expense", "transfer", "reversal", "adjustment"]);
var ENTRY_KIND_LABELS = { income: "Receita", expense: "Despesa", transfer: "Transfer\xEAncia", reversal: "Estorno", adjustment: "Ajuste" };
var billType = z14.enum(["payable", "receivable"]);
var billStatus = z14.enum(["open", "partial", "paid", "overdue", "canceled"]);
var BILL_STATUS_LABELS = { open: "Aberta", partial: "Parcial", paid: "Paga", overdue: "Vencida", canceled: "Cancelada" };
var recurrenceFrequency = z14.enum(["weekly", "monthly", "yearly"]);
var FREQUENCY_LABELS = { weekly: "Semanal", monthly: "Mensal", yearly: "Anual" };
var sourceType = z14.string().regex(/^[a-z][a-z0-9_]{1,39}$/, "Origem inv\xE1lida");
var text2 = (max) => z14.string().trim().max(max);
var optText = (max) => z14.string().trim().max(max).nullish().transform((v) => v ? v : null);
var positiveCents = cents.refine((v) => v > 0, "Informe um valor maior que zero");
var day2 = isoDate.refine((v) => !Number.isNaN(Date.parse(v)), "Data inv\xE1lida");
var optId = uuid.nullish().transform((v) => v ?? null);
var financeCategoryInput = z14.object({
  name: text2(80).min(2, "Informe o nome"),
  kind: categoryKind,
  parentId: optId,
  nature: expenseNature.nullish().transform((v) => v ?? null),
  active: z14.boolean().default(true)
});
var accountInput = z14.object({
  name: text2(80).min(2, "Informe o nome"),
  type: accountType,
  provider: optText(60),
  openingBalanceCents: signedCents.default(0),
  active: z14.boolean().default(true)
});
var entryInput = z14.object({
  type: z14.enum(["income", "expense"]),
  accountId: uuid,
  categoryId: uuid,
  amountCents: positiveCents,
  occurredOn: day2,
  competenceOn: day2.nullish().transform((v) => v ?? null),
  channel: financeChannel.nullish().transform((v) => v ?? null),
  description: text2(200).min(2, "Descreva o lan\xE7amento"),
  counterparty: optText(120),
  notes: optText(500)
});
var transferInput = z14.object({ fromAccountId: uuid, toAccountId: uuid, amountCents: positiveCents, occurredOn: day2, description: optText(200) }).refine((v) => v.fromAccountId !== v.toAccountId, { message: "Escolha contas diferentes", path: ["toAccountId"] });
var reverseInput = z14.object({ reason: text2(300).min(3, "Informe o motivo do estorno"), occurredOn: day2.nullish().transform((v) => v ?? null) });
var adjustInput = z14.object({
  correctAmountCents: positiveCents,
  reason: text2(300).min(3, "Informe o motivo do ajuste"),
  occurredOn: day2.nullish().transform((v) => v ?? null)
});
var rangeOrdered = (v) => !v.from || !v.to || v.from <= v.to;
var rangeWithin = (v) => !v.from || !v.to || (Date.parse(v.to) - Date.parse(v.from)) / 864e5 <= 366;
var entryFilters = z14.object({
  from: day2.optional(),
  to: day2.optional(),
  kind: entryKind.optional(),
  categoryId: uuid.optional(),
  accountId: uuid.optional(),
  channel: financeChannel.optional(),
  reconciled: z14.enum(["yes", "no"]).optional(),
  status: z14.enum(["active", "reversed"]).optional(),
  sourceType: sourceType.optional(),
  q: z14.string().trim().max(120).optional()
});
var entriesExportQuery = entryFilters.refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var entriesListQuery = paginationQuery.extend(entryFilters.shape).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var billInput = z14.object({
  type: billType,
  description: text2(200).min(2, "Descreva a conta"),
  counterparty: optText(120),
  categoryId: uuid,
  accountId: optId,
  channel: financeChannel.nullish().transform((v) => v ?? null),
  amountCents: positiveCents,
  dueOn: day2,
  competenceOn: day2.nullish().transform((v) => v ?? null),
  notes: optText(500)
});
var settleInput = z14.object({
  amountCents: positiveCents,
  paidOn: day2,
  accountId: uuid,
  /** taxa de pagamento descontada do valor recebido (gera despesa própria); só em contas a receber */
  feeCents: cents.default(0),
  notes: optText(300)
});
var cancelBillInput = z14.object({ reason: text2(300).min(3, "Informe o motivo do cancelamento") });
var billFilters = z14.object({
  type: billType,
  status: billStatus.optional(),
  categoryId: uuid.optional(),
  accountId: uuid.optional(),
  channel: financeChannel.optional(),
  from: day2.optional(),
  to: day2.optional(),
  q: z14.string().trim().max(120).optional()
});
var billsExportQuery = billFilters.refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var billsListQuery = paginationQuery.extend(billFilters.shape).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var recurrenceInput = z14.object({
  type: billType.default("payable"),
  description: text2(200).min(2, "Descreva a despesa"),
  counterparty: optText(120),
  categoryId: uuid,
  accountId: optId,
  channel: financeChannel.nullish().transform((v) => v ?? null),
  amountCents: positiveCents,
  frequency: recurrenceFrequency.default("monthly"),
  startOn: day2,
  endOn: day2.nullish().transform((v) => v ?? null),
  active: z14.boolean().default(true)
});
var financePeriodQuery = z14.object({ from: day2, to: day2 }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var financeOverviewQuery = z14.object({ from: day2.optional(), to: day2.optional() }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var GRANULARITIES = ["day", "week", "month"];
var cashFlowQuery = z14.object({ from: day2, to: day2, granularity: z14.enum(GRANULARITIES).default("day"), accountId: uuid.optional(), channel: financeChannel.optional() }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var dreQuery = z14.object({ from: day2, to: day2, channel: financeChannel.optional() }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var dreByChannelQuery = z14.object({ from: day2, to: day2, allocateOverhead: z14.enum(["true", "false"]).default("true").transform((v) => v === "true") }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var compareQuery = z14.object({ from: day2, to: day2, prevFrom: day2.optional(), prevTo: day2.optional(), channel: financeChannel.optional() }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var monthlyQuery = z14.object({ months: z14.coerce.number().int().min(2).max(24).default(6), to: day2.optional() });
var statementQuery = z14.object({ accountId: uuid, from: day2, to: day2 }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var importPreviewInput = z14.object({ accountId: uuid, csv: z14.string().min(1, "Selecione um arquivo CSV").max(2e6, "Arquivo grande demais (m\xE1x. 2 MB)") });
var importConfirmInput = z14.object({
  accountId: uuid,
  filename: text2(160).default("extrato.csv"),
  lines: z14.array(z14.object({ postedOn: day2, description: text2(300), amountCents: signedCents.refine((v) => v !== 0, "Valor zerado"), entryId: uuid.nullish().transform((v) => v ?? null) })).min(1, "Nada para importar").max(2e3, "M\xE1ximo de 2.000 linhas por importa\xE7\xE3o")
});
var matchInput = z14.object({ entryId: uuid, lineId: uuid.nullish().transform((v) => v ?? null) });
var reconciliationQuery = z14.object({ accountId: uuid, from: day2, to: day2, status: z14.enum(["all", "pending", "reconciled"]).default("all") }).refine(rangeOrdered, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine(rangeWithin, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
var closeInput = z14.object({ month: z14.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "M\xEAs inv\xE1lido (AAAA-MM)") });
var reopenInput = z14.object({ reason: text2(300).min(10, "Explique o motivo da reabertura (m\xEDn. 10 caracteres)") });
var postLineSchema = z14.object({
  direction: z14.enum(["in", "out"]),
  amountCents: positiveCents,
  accountId: uuid,
  categoryId: uuid.optional(),
  categoryRole: categoryRole.optional(),
  occurredOn: day2,
  competenceOn: day2.optional(),
  channel: financeChannel.nullish(),
  description: text2(200).min(2),
  counterparty: text2(120).nullish(),
  notes: text2(500).nullish(),
  billId: uuid.nullish()
});

// src/products.ts
import { z as z15 } from "zod";
var PRODUCT_STATUSES = ["rascunho", "em_preparacao", "disponivel", "reservado", "vendido", "em_devolucao", "retirado_de_venda", "arquivado"];
var productStatus = z15.enum(PRODUCT_STATUSES);
var PRODUCT_STATUS_LABEL = {
  rascunho: "Rascunho",
  em_preparacao: "Em prepara\xE7\xE3o",
  disponivel: "Dispon\xEDvel",
  reservado: "Reservado",
  vendido: "Vendido",
  em_devolucao: "Em devolu\xE7\xE3o",
  retirado_de_venda: "Retirado de venda",
  arquivado: "Arquivado"
};
var PRODUCT_TRANSITIONS = {
  rascunho: [
    { to: "em_preparacao", system: false },
    { to: "disponivel", system: false },
    { to: "arquivado", system: false }
  ],
  em_preparacao: [
    { to: "rascunho", system: false },
    { to: "disponivel", system: false },
    { to: "retirado_de_venda", system: false },
    { to: "arquivado", system: false }
  ],
  disponivel: [
    { to: "reservado", system: true },
    { to: "vendido", system: true },
    { to: "em_preparacao", system: false },
    { to: "retirado_de_venda", system: false },
    { to: "arquivado", system: false }
  ],
  reservado: [
    { to: "disponivel", system: true },
    { to: "vendido", system: true }
  ],
  vendido: [{ to: "em_devolucao", system: true }],
  em_devolucao: [
    { to: "disponivel", system: true },
    { to: "retirado_de_venda", system: true },
    { to: "vendido", system: true }
  ],
  retirado_de_venda: [
    { to: "em_preparacao", system: false },
    { to: "disponivel", system: false },
    { to: "arquivado", system: false }
  ],
  arquivado: [{ to: "em_preparacao", system: false }]
};
var manualTargets = (from) => PRODUCT_TRANSITIONS[from].filter((t) => !t.system).map((t) => t.to);
var canTransition = (from, to, opts = {}) => PRODUCT_TRANSITIONS[from].some((t) => t.to === to && (opts.system || !t.system));
var IN_STOCK_STATUSES = ["em_preparacao", "disponivel", "reservado"];
var DEPARTMENTS = ["feminino", "masculino", "infantil", "unissex"];
var DEPARTMENT_LABEL = { feminino: "Feminino", masculino: "Masculino", infantil: "Infantil", unissex: "Unissex" };
var productDepartment = z15.enum(DEPARTMENTS);
var CONDITIONS = ["novo_com_etiqueta", "seminovo", "otimo", "bom", "regular", "com_defeito"];
var CONDITION_LABEL = {
  novo_com_etiqueta: "Novo com etiqueta",
  seminovo: "Seminovo",
  otimo: "\xD3timo",
  bom: "Bom",
  regular: "Regular",
  com_defeito: "Com defeito declarado"
};
var productCondition = z15.enum(CONDITIONS);
var ORIGINS = ["compra_propria", "consignacao", "outra"];
var ORIGIN_LABEL = { compra_propria: "Compra pr\xF3pria", consignacao: "Consigna\xE7\xE3o", outra: "Outra" };
var productOrigin = z15.enum(ORIGINS);
var STANDARD_SIZES = ["PP", "P", "M", "G", "GG", "XG", "UNICO"];
var SKU_REGEX = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/;
var BARCODE_REGEX = /^[A-Za-z0-9-]{4,48}$/;
var cm = z15.number().min(0).max(300).nullable().optional();
var measurementsSchema = z15.object({
  bustCm: cm,
  waistCm: cm,
  hipCm: cm,
  lengthCm: cm,
  sleeveCm: cm
});
var MEASUREMENT_LABEL = { bustCm: "Busto", waistCm: "Cintura", hipCm: "Quadril", lengthCm: "Comprimento", sleeveCm: "Manga" };
function productMargin(priceCents, promoPriceCents, costCents) {
  const effective = promoPriceCents != null && promoPriceCents > 0 && promoPriceCents < priceCents ? promoPriceCents : priceCents;
  const marginCents = effective - costCents;
  const marginPercent = effective > 0 ? Math.round(marginCents / effective * 1e3) / 10 : null;
  return { effectivePriceCents: effective, marginCents, marginPercent };
}
var nullableUuid = uuid.nullable().optional();
var tagList = z15.array(z15.string().trim().min(1).max(30)).max(20).default([]);
var productInput = z15.object({
  name: z15.string().trim().min(2, "Informe o nome da pe\xE7a").max(160),
  description: optionalText(5e3),
  categoryId: nullableUuid,
  brandId: nullableUuid,
  type: optionalText(60),
  department: productDepartment.nullable().optional(),
  sizeLabel: optionalText(20),
  sizeStandard: optionalText(10),
  colorPrimary: optionalText(40),
  colorsSecondary: z15.array(z15.string().trim().min(1).max(40)).max(5).default([]),
  material: optionalText(200),
  condition: productCondition.default("bom"),
  defects: optionalText(1e3),
  measurements: measurementsSchema.default({}),
  costCents: cents.default(0),
  priceCents: cents.default(0),
  promoPriceCents: cents.nullable().optional(),
  /** vazio = gerado pelo contador da loja (prefixo + número) */
  sku: z15.string().trim().regex(SKU_REGEX, "SKU inv\xE1lido (letras, n\xFAmeros, ponto, h\xEDfen; at\xE9 40)").optional().nullable(),
  barcode: z15.string().trim().regex(BARCODE_REGEX, "C\xF3digo de barras inv\xE1lido").optional().nullable(),
  origin: productOrigin.default("compra_propria"),
  supplierId: nullableUuid,
  consignorId: nullableUuid,
  entryDate: isoDate.optional().nullable(),
  tags: tagList,
  internalNotes: optionalText(4e3),
  locationId: nullableUuid
}).superRefine((v, ctx) => {
  if (v.promoPriceCents != null && v.promoPriceCents > 0 && v.promoPriceCents >= v.priceCents) {
    ctx.addIssue({ code: "custom", path: ["promoPriceCents"], message: "O pre\xE7o promocional deve ser menor que o pre\xE7o de venda" });
  }
  if (v.condition === "com_defeito" && !v.defects) {
    ctx.addIssue({ code: "custom", path: ["defects"], message: "Descreva os defeitos da pe\xE7a" });
  }
});
var productStatusInput = z15.object({
  status: productStatus,
  reason: optionalText(300)
});
var boolQuery3 = z15.enum(["true", "false"]).transform((v) => v === "true");
var csvList = z15.string().max(600).transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean));
var intCents = z15.coerce.number().int().min(0).max(1e10);
var productFilters = z15.object({
  q: z15.string().trim().max(120).optional(),
  status: csvList.pipe(z15.array(productStatus)).optional(),
  categoryId: uuid.optional(),
  brandId: uuid.optional(),
  size: z15.string().trim().max(20).optional(),
  color: z15.string().trim().max(40).optional(),
  priceMin: intCents.optional(),
  priceMax: intCents.optional(),
  condition: csvList.pipe(z15.array(productCondition)).optional(),
  origin: productOrigin.optional(),
  locationId: uuid.optional(),
  published: boolQuery3.optional(),
  /** incluir arquivados na lista (padrão: não) */
  archived: boolQuery3.default(false)
});
var productListQuery = paginationQuery.extend(productFilters.shape);
var productExportQuery = productFilters.extend({ sort: z15.string().max(40).optional(), order: z15.enum(["asc", "desc"]).default("asc") });
var PRODUCT_SORT_KEYS = ["createdAt", "name", "sku", "priceCents", "costCents", "status", "entryDate", "publishedAt"];
var productImageUpdate = z15.object({ alt: z15.string().trim().min(1, "Informe o texto alternativo").max(200) });
var productImageReorder = z15.object({ ids: z15.array(uuid).min(1).max(30) });
var categoryInput = z15.object({
  name: z15.string().trim().min(2).max(80),
  parentId: uuid.nullable().optional(),
  active: z15.boolean().default(true)
});
var brandInput = z15.object({ name: z15.string().trim().min(2).max(80), active: z15.boolean().default(true) });
var productBulkInput = z15.object({
  ids: z15.array(uuid).min(1, "Selecione ao menos uma pe\xE7a").max(200),
  patch: z15.object({
    priceCents: cents.optional(),
    /** ajuste percentual sobre o preço atual (ex.: -10 = 10% de desconto) */
    priceAdjustPercent: z15.number().min(-90).max(500).optional(),
    categoryId: uuid.nullable().optional(),
    status: productStatus.optional(),
    published: z15.boolean().optional()
  }).refine((p2) => Object.values(p2).some((v) => v !== void 0), "Informe ao menos uma altera\xE7\xE3o").refine((p2) => !(p2.priceCents !== void 0 && p2.priceAdjustPercent !== void 0), "Use pre\xE7o fixo ou ajuste percentual, n\xE3o os dois")
});
var labelsInput = z15.object({
  ids: z15.array(uuid).min(1).max(500),
  layout: z15.enum(["a4", "rolo"]).default("a4"),
  copies: z15.number().int().min(1).max(5).default(1)
});
var IMPORT_COLUMNS = [
  "nome",
  "sku",
  "categoria",
  "subcategoria",
  "marca",
  "tamanho",
  "cor",
  "conservacao",
  "custo",
  "preco",
  "preco_promocional",
  "descricao",
  "defeitos",
  "material",
  "tipo",
  "departamento",
  "codigo_barras",
  "origem",
  "data_entrada",
  "localizacao",
  "tags"
];
var importCommitInput = z15.object({ skipInvalid: z15.boolean().default(false) });
var publishedListQuery = paginationQuery.extend({
  categoryId: uuid.optional(),
  brandId: uuid.optional(),
  size: z15.string().trim().max(20).optional(),
  color: z15.string().trim().max(40).optional(),
  priceMin: intCents.optional(),
  priceMax: intCents.optional(),
  condition: csvList.pipe(z15.array(productCondition)).optional(),
  promo: boolQuery3.optional()
});

// src/inventory.ts
import { z as z16 } from "zod";
var LOCATION_KINDS = ["arara", "prateleira", "caixa", "deposito"];
var LOCATION_KIND_LABEL = { arara: "Arara", prateleira: "Prateleira", caixa: "Caixa", deposito: "Dep\xF3sito" };
var locationInput = z16.object({
  kind: z16.enum(LOCATION_KINDS),
  name: z16.string().trim().min(1).max(60),
  notes: optionalText(300),
  active: z16.boolean().default(true)
});
var MOVEMENT_TYPES = ["entrada", "saida", "ajuste", "perda", "dano", "extravio", "reserva", "liberacao", "venda", "devolucao"];
var MOVEMENT_LABEL = {
  entrada: "Entrada",
  saida: "Sa\xEDda",
  ajuste: "Ajuste",
  perda: "Perda",
  dano: "Dano",
  extravio: "Extravio",
  reserva: "Reserva",
  liberacao: "Libera\xE7\xE3o",
  venda: "Venda",
  devolucao: "Devolu\xE7\xE3o"
};
var RESERVATION_CHANNELS = ["online", "pdv", "manual"];
var reason = z16.string().trim().min(3, "Informe a justificativa (m\xEDn. 3 caracteres)").max(500);
var stockEntryInput = z16.object({ productId: uuid, reason: optionalText(500), locationId: uuid.nullable().optional() });
var stockWithdrawInput = z16.object({ productId: uuid, reason });
var stockAdjustInput = z16.object({ productId: uuid, delta: z16.union([z16.literal(1), z16.literal(-1)]), reason });
var stockWriteOffInput = z16.object({ productId: uuid, type: z16.enum(["perda", "dano", "extravio"]), reason });
var stockMoveLocationInput = z16.object({ productId: uuid, locationId: uuid.nullable() });
var stockReserveManualInput = z16.object({ productId: uuid, ttlMinutes: z16.number().int().min(1).max(60 * 24 * 7).default(60), note: optionalText(200) });
var movementQuery = paginationQuery.extend({
  productId: uuid.optional(),
  type: z16.string().max(200).transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean)).pipe(z16.array(z16.enum(MOVEMENT_TYPES))).optional(),
  userId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});
var movementExportQuery = movementQuery.omit({ page: true, pageSize: true });
var countStartInput = z16.object({
  name: z16.string().trim().min(2).max(80),
  locationId: uuid.nullable().optional(),
  notes: optionalText(300)
});
var countScanInput = z16.object({ code: z16.string().trim().min(1).max(60), locationId: uuid.nullable().optional() });
var countItemSetInput = z16.object({ counted: z16.boolean() });
var countCloseInput = z16.object({
  /** confirma que itens não contados serão tratados como "não encontrados" */
  confirmMissing: z16.boolean().default(false),
  notes: optionalText(300)
});
var countListQuery = paginationQuery.extend({ status: z16.enum(["aberta", "fechada", "cancelada"]).optional() });
var countItemsQuery = paginationQuery.extend({
  filter: z16.enum(["todos", "pendentes", "contados", "divergentes"]).default("todos")
});
var inventorySettingsInput = z16.object({
  skuPrefix: z16.string().trim().toUpperCase().regex(/^[A-Z0-9]{0,8}$/, "Prefixo: at\xE9 8 letras ou n\xFAmeros"),
  skuPad: z16.number().int().min(1).max(10),
  /** próximo número; só pode aumentar */
  skuNext: z16.number().int().min(1).max(999999999).optional(),
  staleDays: z16.number().int().min(1).max(1e3),
  reservationTtlMinutes: z16.number().int().min(1).max(60 * 24 * 7)
});
var agingQuery = z16.object({ categoryId: uuid.optional() });
var turnoverQuery = z16.object({ from: isoDate.optional(), to: isoDate.optional() });
var staleQuery = paginationQuery.extend({ days: z16.coerce.number().int().min(1).max(1e3).optional() });

// src/storefront.ts
import { z as z17 } from "zod";
var hex = z17.string().trim().regex(/^#[0-9a-fA-F]{6}$/, "Use uma cor no formato #RRGGBB").transform((v) => v.toLowerCase());
var text3 = (max) => z17.string().trim().max(max).refine(noHtml, "Use apenas texto puro (sem HTML)");
var longText = (max = 2e4) => text3(max).nullable().optional().transform((v) => v ? v : null);
var httpUrl = siteUrl.refine((v) => /^https?:\/\//i.test(v), "Informe um endere\xE7o completo (https://...)");
var optionalUrl = httpUrl.nullable().optional().or(z17.literal("").transform(() => null));
var storefrontHostname = z17.string().trim().toLowerCase().max(253).regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/, "Informe s\xF3 o dom\xEDnio, ex.: loja.seudominio.com.br");
var storefrontBannerInput = z17.object({
  assetId: uuid,
  alt: z17.string().trim().min(1, "Informe o texto alternativo do banner").max(200).refine(noHtml, "Use apenas texto puro"),
  title: text3(80).optional().nullable().transform((v) => v || null),
  subtitle: text3(160).optional().nullable().transform((v) => v || null),
  ctaLabel: text3(30).optional().nullable().transform((v) => v || null),
  ctaHref: siteUrl.optional().nullable().or(z17.literal("").transform(() => null))
});
var STOREFRONT_FONT_PAIRS = ["tight", "classic", "modern", "editorial"];
var STOREFRONT_FONT_PAIR_LABEL = {
  tight: "Compacta (padr\xE3o)",
  classic: "Cl\xE1ssica",
  modern: "Moderna",
  editorial: "Editorial"
};
var STOREFRONT_ICONS = ["sparkle", "truck", "swap", "shield", "storefront", "heart", "leaf", "gift", "clock", "star", "package", "chat"];
var STOREFRONT_SECTION_KEYS = ["categories", "newest", "featured", "promotions", "about"];
var STOREFRONT_HERO_LAYOUTS = ["split", "full", "text"];
var STOREFRONT_HERO_HEIGHTS = ["compact", "medium", "tall"];
var STOREFRONT_PRESETS = [
  { id: "oliva", label: "Verde-oliva e creme", primary: "#5c6b2e", secondary: "#c4552d", background: "#faf7f0", fontPair: "tight" },
  { id: "terracota", label: "Terracota", primary: "#a8431f", secondary: "#5c6b2e", background: "#fbf4ec", fontPair: "classic" },
  { id: "grafite", label: "Grafite e \xE2mbar", primary: "#1f1f1d", secondary: "#d98a1c", background: "#f6f4ef", fontPair: "modern" },
  { id: "rosa", label: "Rosa antigo", primary: "#9c4a5a", secondary: "#4a6b5d", background: "#fbf3f3", fontPair: "editorial" },
  { id: "petroleo", label: "Azul petr\xF3leo", primary: "#0f5e63", secondary: "#e08a3c", background: "#f2f7f6", fontPair: "tight" }
];
var PRESET_IDS = STOREFRONT_PRESETS.map((p2) => p2.id);
var homeLink = z17.string().trim().max(300).refine((v) => /^https?:\/\/[^\s<>"'\\]+$/i.test(v) || /^\/(?!\/)[^\s<>"'\\]*$/.test(v), "Use um endere\xE7o https://... ou um caminho da loja (ex.: /catalogo)");
var optLink = homeLink.nullable().optional().or(z17.literal("").transform(() => null)).transform((v) => v || null);
var optText2 = (max) => text3(max).nullable().optional().transform((v) => v || null);
var pct = (def) => z17.number().int().min(0).max(100).default(def);
var storefrontHeroInput = z17.object({
  show: z17.boolean().default(true),
  layout: z17.enum(STOREFRONT_HERO_LAYOUTS).default("split"),
  title: optText2(90),
  /** trecho do título destacado na cor primária (precisa existir no título) */
  highlight: optText2(60),
  subtitle: optText2(200),
  primaryLabel: optText2(30),
  primaryHref: optLink,
  secondaryLabel: optText2(30),
  secondaryHref: optLink,
  imageAssetId: uuid.nullable().optional().transform((v) => v ?? null),
  imageAlt: optText2(200),
  focusX: pct(50),
  focusY: pct(50),
  align: z17.enum(["left", "center"]).default("left"),
  height: z17.enum(STOREFRONT_HERO_HEIGHTS).default("medium"),
  overlay: z17.number().int().min(0).max(80).default(45),
  textTone: z17.enum(["auto", "light", "dark"]).default("auto"),
  /** usa os banners cadastrados como carrossel no lugar do hero (compatibilidade com lojas antigas) */
  useBanners: z17.boolean().default(false)
}).refine((v) => !v.imageAssetId || Boolean(v.imageAlt), { message: "Descreva a imagem do hero (texto alternativo)", path: ["imageAlt"] });
var storefrontBenefitInput = z17.object({
  icon: z17.enum(STOREFRONT_ICONS),
  title: text3(40).min(1, "Informe o t\xEDtulo do benef\xEDcio"),
  subtitle: optText2(80)
});
var storefrontBenefitsInput = z17.object({
  show: z17.boolean().default(true),
  items: z17.array(storefrontBenefitInput).max(5, "No m\xE1ximo 5 benef\xEDcios").default([])
});
var storefrontSectionInput = z17.object({
  key: z17.enum(STOREFRONT_SECTION_KEYS),
  visible: z17.boolean().default(true),
  title: optText2(60)
});
var storefrontHomeInput = z17.object({
  /** última paleta aplicada (só informativo; as cores valem as dos campos primary/secondary/background) */
  preset: z17.enum(PRESET_IDS).nullable().optional().transform((v) => v ?? null),
  fontPair: z17.enum(STOREFRONT_FONT_PAIRS).default("tight"),
  hero: storefrontHeroInput.optional(),
  benefits: storefrontBenefitsInput.optional(),
  sections: z17.array(storefrontSectionInput).max(STOREFRONT_SECTION_KEYS.length).refine((s) => new Set(s.map((x) => x.key)).size === s.length, "Se\xE7\xE3o repetida").optional()
});
var STOREFRONT_DEFAULT_BENEFITS = [
  { icon: "sparkle", title: "Pe\xE7as \xFAnicas e curadas", subtitle: "Escolhidas uma a uma" },
  { icon: "truck", title: "Envio para todo o Brasil", subtitle: "Frete calculado na sacola" },
  { icon: "swap", title: "Troca e devolu\xE7\xE3o", subtitle: "Arrependimento em at\xE9 7 dias" },
  { icon: "shield", title: "Pagamento seguro", subtitle: "Seus dados protegidos" },
  { icon: "storefront", title: "Retirada na loja", subtitle: "Combine com a gente" }
];
var STOREFRONT_DEFAULT_SECTIONS = [
  { key: "categories", visible: true, title: "Procure por tipo de pe\xE7a" },
  { key: "newest", visible: true, title: "Acabou de chegar" },
  { key: "featured", visible: true, title: "Escolhas da casa" },
  { key: "promotions", visible: true, title: "Em promo\xE7\xE3o" },
  { key: "about", visible: true, title: "Sobre a loja" }
];
var STOREFRONT_DEFAULT_HERO_SUBTITLE = "Pe\xE7as \xFAnicas e curadas, escolhidas uma a uma. Quando sai, n\xE3o volta.";
function resolveStorefrontHome(stored, ctx) {
  const h = stored?.hero;
  const configured = Boolean(h);
  const hero = {
    show: h?.show ?? true,
    layout: h?.layout ?? "split",
    title: h?.title || ctx.name,
    highlight: h?.highlight ?? null,
    subtitle: h?.subtitle || ctx.tagline || STOREFRONT_DEFAULT_HERO_SUBTITLE,
    primaryLabel: h?.primaryLabel || "Ver as pe\xE7as",
    primaryHref: h?.primaryLabel ? h.primaryHref || "/catalogo" : "/catalogo",
    secondaryLabel: configured ? h?.secondaryLabel ?? null : "Novidades",
    secondaryHref: configured ? h?.secondaryLabel ? h.secondaryHref || "/catalogo?sort=novidades" : null : "/catalogo?sort=novidades",
    imageAssetId: h?.imageAssetId ?? null,
    imageUrl: h?.imageAssetId ? ctx.heroImageUrl ?? null : null,
    imageAlt: h?.imageAlt ?? "",
    focusX: h?.focusX ?? 50,
    focusY: h?.focusY ?? 50,
    align: h?.align ?? "left",
    height: h?.height ?? "medium",
    overlay: h?.overlay ?? 45,
    textTone: h?.textTone ?? "auto",
    useBanners: configured ? h.useBanners : ctx.hasBanners
  };
  const b = stored?.benefits;
  const benefits = { show: b?.show ?? true, items: b ? b.items.slice(0, 5) : STOREFRONT_DEFAULT_BENEFITS.map((x) => ({ ...x })) };
  const given = new Map((stored?.sections ?? []).map((s) => [s.key, s]));
  const order = [...(stored?.sections ?? []).map((s) => s.key), ...STOREFRONT_DEFAULT_SECTIONS.map((s) => s.key).filter((k) => !given.has(k))];
  const sections = order.map((key) => {
    const def = STOREFRONT_DEFAULT_SECTIONS.find((d) => d.key === key);
    const s = given.get(key);
    return { key, visible: s?.visible ?? def.visible, title: s?.title || def.title };
  });
  return { preset: stored?.preset ?? null, fontPair: stored?.fontPair ?? "tight", hero, benefits, sections };
}
var storefrontSettingsInput = z17.object({
  name: z17.string().trim().min(2, "Informe o nome da loja").max(80).refine(noHtml, "Use apenas texto puro"),
  tagline: text3(120).optional().nullable().transform((v) => v || null),
  primary: hex,
  secondary: hex,
  background: hex,
  logoAssetId: uuid.nullable().optional(),
  banners: z17.array(storefrontBannerInput).max(6).default([]),
  customDomain: storefrontHostname.nullable().optional().or(z17.literal("").transform(() => null)),
  about: longText(),
  privacy: longText(),
  returns: longText(),
  shipping: longText(),
  contactText: longText(2e3),
  address: text3(240).optional().nullable().transform((v) => v || null),
  hours: text3(240).optional().nullable().transform((v) => v || null),
  whatsapp: phoneBR.nullable().optional().or(z17.literal("").transform(() => null)),
  email: emailSchema.nullable().optional().or(z17.literal("").transform(() => null)),
  instagram: optionalUrl,
  facebook: optionalUrl,
  tiktok: optionalUrl,
  seoTitle: text3(70).optional().nullable().transform((v) => v || null),
  seoDescription: text3(160).optional().nullable().transform((v) => v || null),
  /** personalização da home (hero, benefícios, seções, fonte); ausente = mantém o que está gravado */
  home: storefrontHomeInput.optional()
});
var STOREFRONT_ROUTING_STATUSES = ["none", "pending", "active", "error", "unavailable"];
var STOREFRONT_SORTS = ["novidades", "menor_preco", "maior_preco", "relevancia"];
var boolQuery4 = z17.enum(["true", "false"]).transform((v) => v === "true");
var csv2 = (max = 30) => z17.string().transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean).slice(0, max)).pipe(z17.array(z17.string().max(60)));
var storefrontCatalogQuery = z17.object({
  q: z17.string().trim().max(100).optional(),
  /** slug da categoria (inclui subcategorias) */
  category: z17.string().trim().max(100).optional(),
  brand: z17.string().trim().max(80).optional(),
  size: csv2().optional(),
  color: csv2().optional(),
  condition: csv2().pipe(z17.array(z17.enum(CONDITIONS))).optional(),
  priceMin: z17.coerce.number().int().min(0).max(1e8).optional(),
  priceMax: z17.coerce.number().int().min(0).max(1e8).optional(),
  promo: boolQuery4.optional(),
  sort: z17.enum(STOREFRONT_SORTS).optional(),
  page: z17.coerce.number().int().min(1).max(500).default(1),
  pageSize: z17.coerce.number().int().min(1).max(48).default(24)
});
var storefrontCardsQuery = z17.object({
  ids: z17.string().transform((v) => v.split(",").map((x) => x.trim()).filter(Boolean)).pipe(z17.array(uuid).max(50))
});
var storefrontContactInput = z17.object({
  name: z17.string().trim().min(2).max(120),
  email: emailSchema,
  phone: phoneBR.optional().nullable().or(z17.literal("").transform(() => null)),
  message: z17.string().trim().min(5).max(2e3),
  /** produto de origem (slug), opcional */
  productSlug: z17.string().trim().max(200).optional(),
  /** honeypot: humanos não preenchem */
  website: z17.string().max(200).optional(),
  turnstileToken: z17.string().max(2048).optional(),
  acceptPrivacy: z17.literal(true, { message: "Aceite a pol\xEDtica de privacidade para enviar" })
});
var storefrontRegisterInput = z17.object({
  organizationSlug: z17.string().trim().toLowerCase().min(1).max(60),
  name: z17.string().trim().min(2).max(120),
  email: emailSchema,
  password: passwordSchema,
  phone: phoneBR.optional().nullable().or(z17.literal("").transform(() => null)),
  acceptPrivacy: z17.literal(true, { message: "Aceite a pol\xEDtica de privacidade para criar a conta" }),
  /** consentimentos opcionais (padrão: não) */
  marketing: z17.boolean().default(false),
  turnstileToken: z17.string().max(2048).optional(),
  website: z17.string().max(200).optional()
});
var storefrontPreferencesInput = z17.object({
  sizes: z17.array(z17.string().trim().min(1).max(20)).max(12).default([]),
  styles: z17.array(z17.string().trim().min(1).max(40)).max(12).default([])
});
var storefrontFavoriteMergeInput = z17.object({ productIds: z17.array(uuid).max(100) });
var storefrontAddressesInput = z17.object({ addresses: z17.array(customerAddressInput).max(10) });
var storefrontAlertInput = z17.object({
  /** slug da categoria */
  category: z17.string().trim().max(100).optional().nullable(),
  size: z17.string().trim().max(20).optional().nullable(),
  /** consentimento explícito de e-mail e marketing para este alerta */
  consent: z17.literal(true, { message: "Confirme que aceita receber o alerta por e-mail" }),
  textVersion: z17.string().trim().min(1).max(40).default("alerta-novidades-v1")
});

// src/suppliers.ts
import { z as z18 } from "zod";
var boolQuery5 = z18.enum(["true", "false"]).transform((v) => v === "true");
var nullableUuid2 = z18.preprocess((v) => v === "" ? null : v, uuid.nullish().transform((v) => v ?? null));
var supplierDocument = z18.preprocess(
  (v) => typeof v === "string" && v.trim() === "" ? null : v,
  z18.string().transform(onlyDigits).refine((v) => isValidCpf(v) || isValidCnpj(v), "CPF ou CNPJ inv\xE1lido").nullish().transform((v) => v ?? null)
);
var supplierContactInput = z18.object({
  name: z18.string().trim().min(1, "Informe o nome do contato").max(120),
  role: optionalText(60),
  phone: z18.preprocess((v) => v === "" ? null : v, phoneBR.nullish().transform((v) => v ?? null)),
  email: z18.preprocess((v) => v === "" ? null : v, z18.email().max(254).nullish().transform((v) => v ?? null))
});
var supplierAddressInput = addressSchema.partial();
var supplierInput = z18.object({
  name: z18.string().trim().min(2, "Informe o nome do fornecedor").max(120),
  legalName: optionalText(160),
  document: supplierDocument,
  contacts: z18.array(supplierContactInput).max(10).default([]),
  address: supplierAddressInput.nullish().transform((v) => v ?? null),
  notes: optionalText(4e3),
  active: z18.boolean().default(true)
});
var supplierFilters = z18.object({
  q: z18.string().trim().max(120).optional(),
  active: boolQuery5.optional()
});
var supplierListQuery = paginationQuery.extend(supplierFilters.shape);
var supplierExportQuery = supplierFilters;
var SUPPLIER_SORT_KEYS = ["name", "createdAt", "purchasesCount", "totalPurchasedCents", "lastPurchaseOn"];
var PURCHASE_STATUSES = ["draft", "ordered", "partially_received", "received", "canceled"];
var purchaseStatus = z18.enum(PURCHASE_STATUSES);
var PURCHASE_STATUS_LABELS = {
  draft: "Rascunho",
  ordered: "Pedido feito",
  partially_received: "Recebida em parte",
  received: "Recebida",
  canceled: "Cancelada"
};
var PURCHASE_ALLOCATION_METHODS = ["equal", "proportional"];
var purchaseAllocationMethod = z18.enum(PURCHASE_ALLOCATION_METHODS);
var PURCHASE_ALLOCATION_LABELS = {
  equal: "Igual para todas as pe\xE7as",
  proportional: "Proporcional ao pre\xE7o sugerido"
};
var PURCHASE_COST_KINDS = ["freight", "fee", "other"];
var purchaseCostKind = z18.enum(PURCHASE_COST_KINDS);
var PURCHASE_COST_LABELS = { freight: "Frete", fee: "Taxa", other: "Outro custo" };
var purchaseItemInput = z18.object({
  description: z18.string().trim().min(2, "Descreva o item").max(150),
  quantity: z18.number().int().min(1).max(500),
  categoryId: nullableUuid2,
  /** preço de venda sugerido POR PEÇA (base do rateio proporcional) */
  suggestedPriceCents: cents.default(0)
});
var purchaseCostInput = z18.object({
  kind: purchaseCostKind,
  description: z18.string().trim().min(2, "Descreva o custo").max(120),
  amountCents: cents.refine((v) => v > 0, "Informe um valor maior que zero")
});
var purchaseInstallmentInput = z18.object({
  dueOn: isoDate,
  amountCents: cents.refine((v) => v > 0, "Informe um valor maior que zero")
});
var purchaseInstallmentPlan = z18.object({
  count: z18.number().int().min(1).max(36),
  firstDueOn: isoDate,
  intervalDays: z18.number().int().min(1).max(120).default(30)
});
var purchaseInput = z18.object({
  supplierId: uuid,
  purchasedOn: isoDate,
  /** custo do lote (valor negociado com o fornecedor) */
  lotCostCents: cents,
  allocationMethod: purchaseAllocationMethod.default("proportional"),
  notes: optionalText(2e3),
  items: z18.array(purchaseItemInput).min(1, "Inclua ao menos um item").max(100),
  /** custos adicionais (frete, taxas...) */
  costs: z18.array(purchaseCostInput).max(20).default([]),
  /** parcelas manuais: a soma TEM de bater com o custo total (lote + adicionais) */
  installments: z18.array(purchaseInstallmentInput).max(36).optional(),
  /** ou plano automático (ignorado se `installments` vier) */
  installmentPlan: purchaseInstallmentPlan.optional()
});
var purchaseInstallmentsInput = z18.object({
  installments: z18.array(purchaseInstallmentInput).max(36).optional(),
  installmentPlan: purchaseInstallmentPlan.optional()
}).refine((v) => v.installments || v.installmentPlan, { message: "Informe as parcelas ou um plano de parcelamento" });
var purchaseCancelInput = z18.object({ reason: z18.string().trim().min(3, "Informe o motivo do cancelamento").max(300) });
var purchasePayInstallmentInput = z18.object({
  paidOn: isoDate,
  accountId: uuid,
  notes: optionalText(300)
});
var rangeOrdered2 = (v) => !v.from || !v.to || v.from <= v.to;
var purchaseFilters = z18.object({
  q: z18.string().trim().max(120).optional(),
  status: purchaseStatus.optional(),
  supplierId: uuid.optional(),
  /** período pela data da compra */
  from: isoDate.optional(),
  to: isoDate.optional()
});
var purchaseListQuery = paginationQuery.extend(purchaseFilters.shape).refine(rangeOrdered2, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var purchaseExportQuery = purchaseFilters.extend({ sort: z18.string().max(40).optional(), order: z18.enum(["asc", "desc"]).default("desc") }).refine(rangeOrdered2, { message: "Per\xEDodo inv\xE1lido", path: ["to"] });
var PURCHASE_SORT_KEYS = ["number", "purchasedOn", "totalCostCents", "status", "supplierName", "createdAt"];
var purchaseReceivePiece = z18.object({
  name: optionalText(160),
  sizeLabel: optionalText(20),
  condition: productCondition.optional(),
  priceCents: cents.optional(),
  colorPrimary: optionalText(40),
  brandId: nullableUuid2,
  /** obrigatório quando a conservação é "com defeito" */
  defects: optionalText(1e3)
});
var purchaseReceiveInput = z18.object({
  items: z18.array(
    z18.object({
      purchaseItemId: uuid,
      /** uma entrada por peça física recebida (objeto vazio = só contar a peça) */
      pieces: z18.array(purchaseReceivePiece).min(1).max(500)
    })
  ).min(1).max(100)
});
var INSPECTION_STATUSES = ["pending", "approved", "rejected"];
var inspectionStatus = z18.enum(INSPECTION_STATUSES);
var INSPECTION_LABELS = { pending: "Aguardando inspe\xE7\xE3o", approved: "Aprovada", rejected: "Reprovada" };
var PIECE_RESOLUTIONS = ["loss", "return_to_supplier"];
var pieceResolution = z18.enum(PIECE_RESOLUTIONS);
var PIECE_RESOLUTION_LABELS = { loss: "Perda", return_to_supplier: "Devolu\xE7\xE3o ao fornecedor" };
var purchaseRejectInput = z18.object({
  reason: z18.string().trim().min(3, "Informe o motivo da reprova\xE7\xE3o").max(300),
  resolution: pieceResolution
});
var purchaseLabelsInput = z18.object({
  /** ids de `supplier_purchase_pieces`; vazio = todas as peças recebidas da compra */
  pieceIds: z18.array(uuid).max(500).optional(),
  layout: z18.enum(["a4", "rolo"]).default("a4"),
  copies: z18.number().int().min(1).max(5).default(1)
});
var purchaseImportCommitInput = z18.object({
  /** id devolvido pelo preview */
  batchId: uuid,
  supplierId: uuid,
  skipInvalid: z18.boolean().default(false)
});

// src/consignments.ts
import { z as z19 } from "zod";
var boolQuery6 = z19.enum(["true", "false"]).transform((v) => v === "true");
var CONSIGNOR_STATUSES = ["active", "inactive", "blocked"];
var consignorStatus = z19.enum(CONSIGNOR_STATUSES);
var CONSIGNOR_STATUS_LABELS = { active: "Ativo", inactive: "Inativo", blocked: "Bloqueado" };
var COMMISSION_TYPES = ["percent", "fixed"];
var consignmentCommissionType = z19.enum(COMMISSION_TYPES);
var FEE_MODES = ["none", "proportional", "fixed"];
var consignmentFeeMode = z19.enum(FEE_MODES);
var FEE_MODE_LABELS = {
  none: "A loja absorve a taxa de pagamento",
  proportional: "Taxa de pagamento repartida proporcionalmente",
  fixed: "Taxa fixa por pe\xE7a vendida"
};
var BATCH_STATUSES = ["draft", "active", "finished", "canceled"];
var consignmentBatchStatus = z19.enum(BATCH_STATUSES);
var BATCH_STATUS_LABELS = { draft: "Rascunho", active: "Ativo", finished: "Encerrado", canceled: "Cancelado" };
var ITEM_STATUSES = ["active", "sold", "returned", "canceled"];
var consignmentItemStatus = z19.enum(ITEM_STATUSES);
var ITEM_STATUS_LABELS = { active: "Em estoque", sold: "Vendida", returned: "Devolvida", canceled: "Removida do lote" };
var LEDGER_KINDS = ["sale", "reversal", "payout", "adjustment"];
var consignorLedgerKind = z19.enum(LEDGER_KINDS);
var LEDGER_KIND_LABELS = { sale: "Venda", reversal: "Estorno de venda", payout: "Repasse", adjustment: "Ajuste" };
var SETTLEMENT_STATUSES = ["open", "conferred", "closed"];
var consignmentSettlementStatus = z19.enum(SETTLEMENT_STATUSES);
var SETTLEMENT_STATUS_LABELS = { open: "Aberto", conferred: "Conferido", closed: "Fechado" };
var PAYOUT_METHODS = ["pix", "transfer", "cash", "other"];
var consignmentPayoutMethod = z19.enum(PAYOUT_METHODS);
var PAYOUT_METHOD_LABELS = { pix: "Pix", transfer: "Transfer\xEAncia", cash: "Dinheiro", other: "Outro" };
var PIX_KEY_TYPES = ["cpf", "cnpj", "email", "phone", "random"];
var consignorPixKeyType = z19.enum(PIX_KEY_TYPES);
var bps2 = z19.number().int().min(0).max(1e4);
var consignorDocument = z19.string().transform(onlyDigits).superRefine((v, ctx) => {
  if (v.length === 11) {
    if (!cpfBR.safeParse(v).success) ctx.addIssue({ code: "custom", message: "CPF inv\xE1lido" });
  } else if (v.length === 14) {
    if (!cnpjBR.safeParse(v).success) ctx.addIssue({ code: "custom", message: "CNPJ inv\xE1lido" });
  } else ctx.addIssue({ code: "custom", message: "Informe um CPF (11 d\xEDgitos) ou CNPJ (14 d\xEDgitos) v\xE1lido" });
});
var consignorInput = z19.object({
  name: z19.string().trim().min(2, "Informe o nome").max(120),
  document: consignorDocument,
  email: z19.email("E-mail inv\xE1lido").max(254).optional().nullable().or(z19.literal("").transform(() => null)),
  phone: phoneBR.optional().nullable().or(z19.literal("").transform(() => null)),
  whatsapp: phoneBR.optional().nullable().or(z19.literal("").transform(() => null)),
  address: addressSchema.optional().nullable(),
  notes: optionalText(2e3),
  status: consignorStatus.default("active")
});
var consignorFilters = z19.object({
  q: z19.string().trim().max(120).optional(),
  status: consignorStatus.optional(),
  hasBalance: boolQuery6.optional(),
  deleted: boolQuery6.default(false)
});
var consignorListQuery = paginationQuery.extend(consignorFilters.shape);
var consignorExportQuery = consignorFilters;
var consignorPaymentInput = z19.object({
  pixKeyType: consignorPixKeyType.optional().nullable(),
  pixKey: z19.string().trim().max(140).optional().nullable(),
  bankName: z19.string().trim().max(80).optional().nullable(),
  agency: z19.string().trim().max(10).optional().nullable(),
  account: z19.string().trim().max(20).optional().nullable(),
  accountType: z19.enum(["checking", "savings"]).optional().nullable(),
  holderName: z19.string().trim().max(120).optional().nullable(),
  holderDocument: consignorDocument.optional().nullable()
}).superRefine((v, ctx) => {
  const hasPix = !!(v.pixKey && v.pixKeyType);
  const hasBank = !!(v.bankName && v.account);
  if (!hasPix && !hasBank) ctx.addIssue({ code: "custom", path: ["pixKey"], message: "Informe a chave Pix ou os dados da conta banc\xE1ria" });
  if (v.pixKey && !v.pixKeyType) ctx.addIssue({ code: "custom", path: ["pixKeyType"], message: "Informe o tipo da chave Pix" });
  if (v.pixKey && v.pixKeyType) {
    const k = v.pixKey.trim();
    const ok = v.pixKeyType === "cpf" ? cpfBR.safeParse(k).success : v.pixKeyType === "cnpj" ? cnpjBR.safeParse(k).success : v.pixKeyType === "email" ? z19.email().safeParse(k).success : v.pixKeyType === "phone" ? /^\+?\d{10,13}$/.test(k.replace(/[\s()-]/g, "")) : k.length >= 8;
    if (!ok) ctx.addIssue({ code: "custom", path: ["pixKey"], message: "Chave Pix inv\xE1lida para o tipo informado" });
  }
});
var consignmentSettingsInput = z19.object({
  defaultCommissionType: consignmentCommissionType,
  /** bps (percent) ou centavos (fixed) */
  defaultCommissionValue: z19.number().int().min(0).max(1e10),
  defaultStayDays: z19.number().int().min(1).max(730),
  feeMode: consignmentFeeMode,
  feeFixedCents: cents.default(0),
  discountEnabledDefault: z19.boolean(),
  discountAfterDaysDefault: z19.number().int().min(1).max(730),
  discountMaxBpsDefault: bps2
}).superRefine((v, ctx) => {
  if (v.defaultCommissionType === "percent" && v.defaultCommissionValue > 1e4) ctx.addIssue({ code: "custom", path: ["defaultCommissionValue"], message: "A comiss\xE3o em % n\xE3o pode passar de 100%" });
  if (v.feeMode === "fixed" && v.feeFixedCents <= 0) ctx.addIssue({ code: "custom", path: ["feeFixedCents"], message: "Informe o valor da taxa fixa" });
});
var consignmentDiscountRule = z19.object({
  enabled: z19.boolean(),
  afterDays: z19.number().int().min(1).max(730),
  maxBps: bps2,
  /** data em que o consignante concordou com o desconto automático (obrigatória para ativar o lote quando habilitado) */
  agreedAt: isoDate.nullable().optional(),
  agreedNote: optionalText(300)
});
var consignmentReturnRule = z19.object({
  text: optionalText(2e3),
  allowEarlyReturn: z19.boolean().default(true),
  returnFeeNote: optionalText(300)
});
var commissionFields = {
  commissionType: consignmentCommissionType.optional(),
  /** bps (percent) ou centavos (fixed) */
  commissionValue: z19.number().int().min(0).max(1e10).optional()
};
function checkCommission(v, ctx) {
  if (v.commissionType === void 0 !== (v.commissionValue === void 0)) ctx.addIssue({ code: "custom", path: ["commissionValue"], message: "Informe o tipo e o valor da comiss\xE3o juntos" });
  if (v.commissionType === "percent" && (v.commissionValue ?? 0) > 1e4) ctx.addIssue({ code: "custom", path: ["commissionValue"], message: "A comiss\xE3o em % n\xE3o pode passar de 100%" });
}
var consignmentNewProduct = z19.object({
  name: z19.string().trim().min(2, "Informe o nome da pe\xE7a").max(160),
  categoryId: uuid,
  brandId: uuid.optional().nullable(),
  sizeLabel: z19.string().trim().min(1).max(20),
  condition: productCondition.default("bom"),
  colorPrimary: z19.string().trim().max(40).optional().nullable(),
  defects: z19.string().trim().max(1e3).optional().nullable(),
  /** preço de venda ao público; padrão = preço acordado */
  priceCents: cents.optional(),
  locationId: uuid.optional().nullable()
});
var consignmentItemInput = z19.object({
  productId: uuid.optional(),
  newProduct: consignmentNewProduct.optional(),
  /** preço de venda acordado com o consignante; padrão = preço da peça */
  agreedPriceCents: cents.optional(),
  ...commissionFields
}).superRefine((v, ctx) => {
  if (!!v.productId === !!v.newProduct) ctx.addIssue({ code: "custom", path: ["productId"], message: "Informe uma pe\xE7a existente ou os dados de uma pe\xE7a nova" });
  if (v.newProduct && v.agreedPriceCents === void 0 && v.newProduct.priceCents === void 0) ctx.addIssue({ code: "custom", path: ["agreedPriceCents"], message: "Informe o pre\xE7o acordado" });
  checkCommission(v, ctx);
});
var consignmentBatchInput = z19.object({
  consignorId: uuid,
  receivedOn: isoDate.optional(),
  stayDays: z19.number().int().min(1).max(730).optional(),
  ...commissionFields,
  discountRule: consignmentDiscountRule.optional(),
  returnRule: consignmentReturnRule.optional(),
  notes: optionalText(2e3),
  items: z19.array(consignmentItemInput).max(300).default([])
}).superRefine(checkCommission);
var consignmentBatchUpdateInput = z19.object({
  receivedOn: isoDate.optional(),
  stayDays: z19.number().int().min(1).max(730).optional(),
  ...commissionFields,
  discountRule: consignmentDiscountRule.optional(),
  returnRule: consignmentReturnRule.optional(),
  notes: optionalText(2e3)
}).superRefine(checkCommission);
var consignmentBatchSignedInput = z19.object({ signedAt: isoDate });
var consignmentBatchListQuery = paginationQuery.extend({
  status: consignmentBatchStatus.optional(),
  consignorId: uuid.optional()
});
var csvStatuses = z19.string().max(80).transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean)).pipe(z19.array(consignmentItemStatus));
var consignmentItemListQuery = paginationQuery.extend({
  status: csvStatuses.optional()
});
var overdueFilters = z19.object({
  q: z19.string().trim().max(120).optional(),
  consignorId: uuid.optional(),
  batchId: uuid.optional(),
  /** só as vencidas há pelo menos N dias */
  minDaysOverdue: z19.coerce.number().int().min(0).max(3650).optional(),
  /** inclui também as que vencem nos próximos N dias (padrão 0 = só vencidas) */
  dueWithinDays: z19.coerce.number().int().min(0).max(365).default(0)
});
var consignmentOverdueQuery = paginationQuery.extend(overdueFilters.shape);
var consignmentOverdueExportQuery = overdueFilters;
var ledgerFilters = z19.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  kind: consignorLedgerKind.optional()
});
var consignorStatementQuery = paginationQuery.extend(ledgerFilters.shape);
var consignorStatementExportQuery = ledgerFilters;
var consignorAdjustmentInput = z19.object({
  amountCents: signedCents.refine((v) => v !== 0, "O valor do ajuste n\xE3o pode ser zero"),
  reason: z19.string().trim().min(5, "Justifique o ajuste (m\xEDnimo 5 caracteres)").max(300),
  occurredOn: isoDate.optional()
});
var consignmentSettlementInput = z19.object({ consignorId: uuid, periodFrom: isoDate, periodTo: isoDate }).refine((v) => v.periodFrom <= v.periodTo, { path: ["periodTo"], message: "O fim do per\xEDodo deve ser igual ou posterior ao in\xEDcio" });
var consignmentSettlementListQuery = paginationQuery.extend({
  status: consignmentSettlementStatus.optional(),
  consignorId: uuid.optional()
});
var consignmentPayoutInput = z19.object({
  consignorId: uuid,
  settlementId: uuid.optional().nullable(),
  amountCents: cents.refine((v) => v > 0, "O valor deve ser maior que zero"),
  method: consignmentPayoutMethod,
  paidOn: isoDate,
  /** conta financeira de saída */
  accountId: uuid,
  note: optionalText(300)
});
var consignmentPayoutListQuery = paginationQuery.extend({
  consignorId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});
var consignmentReturnInput = z19.object({
  consignorId: uuid,
  batchId: uuid.optional().nullable(),
  reason: z19.string().trim().min(3, "Informe o motivo da devolu\xE7\xE3o").max(300),
  returnedOn: isoDate.optional(),
  /** ids dos itens de lote (consignment_batch_items) a devolver */
  itemIds: z19.array(uuid).min(1, "Selecione ao menos uma pe\xE7a").max(300)
});
var consignmentReturnListQuery = paginationQuery.extend({ consignorId: uuid.optional() });

// src/returns.ts
import { z as z20 } from "zod";
var RETURN_REASONS = ["arrependimento", "defeito_nao_declarado", "tamanho_medida", "divergencia_anuncio", "outro"];
var returnReason = z20.enum(RETURN_REASONS);
var RETURN_REASON_LABELS = {
  arrependimento: "Arrependimento da compra",
  defeito_nao_declarado: "Defeito n\xE3o declarado",
  tamanho_medida: "Tamanho ou medida diferente do esperado",
  divergencia_anuncio: "Pe\xE7a diferente do an\xFAncio",
  outro: "Outro motivo"
};
var RETURN_LEGAL_REASONS = ["arrependimento", "defeito_nao_declarado", "divergencia_anuncio"];
var RETURN_LEGAL = { withdrawalDays: 7, defectDays: 30 };
var RETURN_RESOLUTIONS = ["refund", "store_credit", "exchange"];
var returnResolution = z20.enum(RETURN_RESOLUTIONS);
var RETURN_RESOLUTION_LABELS = { refund: "Reembolso", store_credit: "Cr\xE9dito da loja", exchange: "Troca" };
var RETURN_SHIPPING_PAYERS = ["store", "by_reason", "customer"];
var RETURN_SHIPPING_PAYER_LABELS = {
  store: "A loja paga sempre",
  by_reason: "A loja paga nos motivos legais; o cliente paga em tamanho/outro",
  customer: "O cliente paga sempre"
};
var RETURN_STATUSES = ["solicitada", "em_analise", "aprovada", "recusada", "aguardando_envio", "em_transito", "recebida", "em_inspecao", "concluida_reembolso", "concluida_troca", "concluida_credito", "cancelada"];
var returnStatus = z20.enum(RETURN_STATUSES);
var RETURN_STATUS_LABELS = {
  solicitada: "Solicitada",
  em_analise: "Em an\xE1lise",
  aprovada: "Aprovada",
  recusada: "Recusada",
  aguardando_envio: "Aguardando envio",
  em_transito: "Em tr\xE2nsito",
  recebida: "Recebida",
  em_inspecao: "Em inspe\xE7\xE3o",
  concluida_reembolso: "Conclu\xEDda (reembolso)",
  concluida_troca: "Conclu\xEDda (troca)",
  concluida_credito: "Conclu\xEDda (cr\xE9dito)",
  cancelada: "Cancelada"
};
var RETURN_TERMINAL = ["recusada", "concluida_reembolso", "concluida_troca", "concluida_credito", "cancelada"];
var RETURN_ACTIVE_STATUSES = RETURN_STATUSES.filter((s) => !RETURN_TERMINAL.includes(s));
var RETURN_TRANSITIONS = {
  solicitada: ["em_analise", "aprovada", "recusada", "cancelada"],
  em_analise: ["aprovada", "recusada", "cancelada"],
  aprovada: ["aguardando_envio", "recebida", "cancelada"],
  aguardando_envio: ["em_transito", "recebida", "cancelada"],
  em_transito: ["recebida", "cancelada"],
  recebida: ["em_inspecao"],
  em_inspecao: ["concluida_reembolso", "concluida_troca", "concluida_credito", "cancelada"],
  recusada: [],
  concluida_reembolso: [],
  concluida_troca: [],
  concluida_credito: [],
  cancelada: []
};
var canReturnTransition = (from, to) => RETURN_TRANSITIONS[from].includes(to);
var RETURN_CONDITIONS = ["perfeita", "uso_leve", "danificada", "fora_do_padrao"];
var returnCondition = z20.enum(RETURN_CONDITIONS);
var RETURN_CONDITION_LABELS = {
  perfeita: "Em \xF3timo estado, como foi enviada",
  uso_leve: "Sinais leves de uso (aceit\xE1vel)",
  danificada: "Danificada (mancha, rasgo, odor)",
  fora_do_padrao: "N\xE3o \xE9 a pe\xE7a enviada / sem etiqueta"
};
var RETURN_DECISIONS = ["restock", "loss", "return_to_customer"];
var returnDecision = z20.enum(RETURN_DECISIONS);
var RETURN_DECISION_LABELS = {
  restock: "Volta ao estoque",
  loss: "Pe\xE7a danificada: registrar perda",
  return_to_customer: "Devolver a pe\xE7a ao cliente (sem reembolso)"
};
var RETURN_REFUND_METHODS = ["provider", "manual"];
var RETURN_MANUAL_METHODS = ["cash", "pix", "bank_transfer"];
var RETURN_MANUAL_METHOD_LABELS = { cash: "Dinheiro", pix: "PIX", bank_transfer: "Transfer\xEAncia" };
var RETURN_CHANNELS = ["account", "guest_link", "admin", "pdv", "whatsapp"];
var RETURN_CHANNEL_LABELS = { account: "Conta do cliente", guest_link: "Link do pedido", admin: "Painel", pdv: "PDV", whatsapp: "WhatsApp" };
var returnPolicyInput = z20.object({
  enabled: z20.boolean().default(true),
  /** prazo (dias corridos do recebimento) para arrependimento e motivos voluntários */
  windowDays: z20.number().int().min(1).max(90).default(7),
  /** prazo para defeito não declarado e divergência do anúncio (garantia legal) */
  defectWindowDays: z20.number().int().min(1).max(365).default(30),
  acceptedReasons: z20.array(returnReason).min(1).default([...RETURN_REASONS]),
  shippingPayer: z20.enum(RETURN_SHIPPING_PAYERS).default("store"),
  allowedResolutions: z20.array(returnResolution).min(1).default([...RETURN_RESOLUTIONS]),
  /** frete original volta nos motivos NÃO legais (nos legais volta sempre) */
  refundShippingVoluntary: z20.boolean().default(false),
  requirePhotosForDefect: z20.boolean().default(true),
  notifyEmail: z20.boolean().default(true),
  notifyWhatsapp: z20.boolean().default(true),
  conditions: z20.string().trim().max(2e3).default(""),
  policyText: z20.string().trim().max(8e3).default("")
});
var DEFAULT_RETURN_POLICY = returnPolicyInput.parse({});
function returnPolicyViolations(p2) {
  const out = [];
  if (p2.windowDays < RETURN_LEGAL.withdrawalDays) out.push(`O prazo de arrependimento n\xE3o pode ser menor que ${RETURN_LEGAL.withdrawalDays} dias corridos do recebimento (CDC, art. 49).`);
  if (p2.defectWindowDays < RETURN_LEGAL.defectDays) out.push(`O prazo para defeito n\xE3o declarado n\xE3o pode ser menor que ${RETURN_LEGAL.defectDays} dias (garantia legal, CDC, art. 26).`);
  const missing = RETURN_LEGAL_REASONS.filter((r) => !p2.acceptedReasons.includes(r));
  if (missing.length) out.push(`Os motivos protegidos por lei n\xE3o podem ser desativados: ${missing.map((r) => RETURN_REASON_LABELS[r]).join(", ")}.`);
  if (p2.shippingPayer === "customer") out.push("No arrependimento e no defeito, o frete de devolu\xE7\xE3o \xE9 custo do fornecedor: o cliente n\xE3o pode pagar sempre.");
  if (!p2.allowedResolutions.includes("refund")) out.push("O reembolso em dinheiro precisa continuar dispon\xEDvel (direito do consumidor nos motivos legais).");
  return out;
}
function returnWindowDays(p2, reason3) {
  if (reason3 === "defeito_nao_declarado" || reason3 === "divergencia_anuncio") return Math.max(p2.defectWindowDays, RETURN_LEGAL.defectDays);
  return Math.max(p2.windowDays, RETURN_LEGAL.withdrawalDays);
}
function returnShippingBearer(p2, reason3) {
  if (RETURN_LEGAL_REASONS.includes(reason3)) return "store";
  return p2.shippingPayer === "store" ? "store" : "customer";
}
var text4 = (min, max, msg) => z20.string().trim().min(min, msg).max(max);
var returnCreateInput = z20.object({
  orderId: uuid,
  orderItemIds: z20.array(uuid).min(1, "Escolha ao menos uma pe\xE7a").max(30),
  reason: returnReason,
  description: text4(0, 2e3, "").default(""),
  resolution: returnResolution.default("refund"),
  /** só o painel: de onde veio o pedido de devolução */
  channel: z20.enum(["admin", "pdv", "whatsapp"]).default("admin")
});
var returnCustomerCreateInput = returnCreateInput.omit({ orderId: true, channel: true }).extend({ orderId: uuid });
var returnTransitionInput = z20.object({
  to: returnStatus,
  reason: text4(0, 500, "").optional(),
  trackingCode: z20.string().trim().min(3).max(60).optional(),
  /** instruções de envio (aprovada → aguardando envio) */
  instructions: text4(0, 1e3, "").optional()
});
var returnReceiveInput = z20.object({ note: text4(0, 500, "").optional() });
var returnInspectionInput = z20.object({
  items: z20.array(
    z20.object({
      returnItemId: uuid,
      condition: returnCondition,
      decision: returnDecision,
      note: text4(0, 500, "").optional(),
      /** onde a peça volta: disponível (já publicável) ou em preparação (para republicar) */
      restockTarget: z20.enum(["disponivel", "em_preparacao"]).default("em_preparacao"),
      locationId: uuid.nullable().optional()
    })
  ).min(1),
  /** justificativa obrigatória quando alguma peça é reprovada */
  justification: text4(0, 500, "").optional()
});
var returnResolveInput = z20.object({
  resolution: returnResolution,
  refundMethod: z20.enum(RETURN_REFUND_METHODS).default("manual"),
  manualMethod: z20.enum(RETURN_MANUAL_METHODS).optional(),
  /** reembolso parcial: valor das peças (sem frete); padrão = tudo que foi aceito na inspeção */
  amountCents: cents.optional(),
  includeShipping: z20.boolean().optional(),
  note: text4(0, 500, "").optional(),
  /** troca: peça de reposição (opcional; fica reservada ao cliente) */
  replacementProductId: uuid.optional()
}).refine((v) => v.resolution !== "refund" || v.refundMethod !== "manual" || Boolean(v.manualMethod), { message: "Informe como o valor foi devolvido (dinheiro, PIX ou transfer\xEAncia)", path: ["manualMethod"] });
var returnMessageInput = z20.object({ body: text4(1, 2e3, "Escreva a mensagem") });
var returnCancelByCustomerInput = z20.object({ reason: text4(0, 500, "").optional() });
var csv3 = (values) => z20.string().max(300).transform((v) => v.split(",").map((s) => s.trim()).filter(Boolean)).pipe(z20.array(z20.enum(values)));
var returnListQuery = paginationQuery.extend({
  status: csv3(RETURN_STATUSES).optional(),
  reason: csv3(RETURN_REASONS).optional(),
  resolution: csv3(RETURN_RESOLUTIONS).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  orderId: uuid.optional(),
  customerId: uuid.optional()
});
var returnReportQuery = z20.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  reason: csv3(RETURN_REASONS).optional(),
  categoryId: uuid.optional()
});
var STORE_CREDIT_KINDS = ["issue_return", "issue_exchange", "use_order", "use_pos", "reversal", "adjustment"];
var STORE_CREDIT_KIND_LABELS = {
  issue_return: "Cr\xE9dito de devolu\xE7\xE3o",
  issue_exchange: "Cr\xE9dito de troca",
  use_order: "Usado em pedido",
  use_pos: "Usado no PDV",
  reversal: "Estorno de uso",
  adjustment: "Ajuste manual"
};
var storeCreditAdjustInput = z20.object({
  customerId: uuid,
  amountCents: z20.number().int().min(-1e7).max(1e7).refine((v) => v !== 0, "Informe um valor diferente de zero"),
  reason: text4(5, 300, "Informe a justificativa (m\xEDn. 5 caracteres)")
});

// src/pos.ts
import { z as z21 } from "zod";
var POS_PAYMENT_METHODS = ["cash", "credit_card", "debit_card", "pix", "bank_transfer"];
var posPaymentMethod = z21.enum(POS_PAYMENT_METHODS);
var POS_PAYMENT_LABELS = {
  cash: "Dinheiro",
  credit_card: "Cart\xE3o de cr\xE9dito",
  debit_card: "Cart\xE3o de d\xE9bito",
  pix: "PIX",
  bank_transfer: "Transfer\xEAncia"
};
var POS_SALE_STATUSES = ["completed", "canceled", "partially_returned", "returned"];
var POS_SALE_STATUS_LABELS = {
  completed: "Conclu\xEDda",
  canceled: "Cancelada",
  partially_returned: "Devolu\xE7\xE3o parcial",
  returned: "Devolvida"
};
var POS_NO_FISCAL_TEXT = "Documento sem valor fiscal";
var csvOf = (values) => z21.string().transform((s) => s.split(",").map((x) => x.trim()).filter(Boolean)).pipe(z21.array(z21.enum(values)));
var bps3 = z21.number().int().min(0).max(3e3);
var posSettingsInput = z21.object({
  /** janela (min) em que a venda pode ser cancelada com a peça ainda na loja; 0 = nunca */
  cancelWindowMinutes: z21.number().int().min(0).max(1440).default(60),
  /** validade da reserva da peça no carrinho; o front renova enquanto a venda está aberta */
  cartReservationMinutes: z21.number().int().min(5).max(120).default(15),
  /** validade da reserva de venda suspensa */
  holdReservationMinutes: z21.number().int().min(15).max(1440).default(240),
  creditFeeBps: bps3.default(0),
  debitFeeBps: bps3.default(0),
  pixFeeBps: bps3.default(0),
  transferFeeBps: bps3.default(0),
  cashAccountId: uuid.nullish(),
  bankAccountId: uuid.nullish(),
  receiptFooter: optionalText(300),
  receiptFormat: z21.enum(["80mm", "a4"]).default("80mm")
});
var posPinInput = z21.object({ pin: z21.string().regex(/^\d{4,8}$/, "O PIN deve ter de 4 a 8 d\xEDgitos") });
var posOpenShiftInput = z21.object({
  terminal: z21.string().trim().min(1, "Informe o terminal").max(40),
  openingCents: cents.default(0),
  note: optionalText(300)
});
var posCashMovementInput = z21.object({
  kind: z21.enum(["sangria", "suprimento"]),
  amountCents: cents.min(1, "Informe um valor maior que zero"),
  reason: z21.string().trim().min(3, "Informe o motivo").max(300)
});
var posCloseShiftInput = z21.object({
  /** contagem por forma de pagamento (dinheiro na gaveta; demais conforme maquininha/extrato) */
  counted: z21.object({ cash: cents, credit_card: cents, debit_card: cents, pix: cents, bank_transfer: cents }),
  note: optionalText(500)
});
var posShiftListQuery = paginationQuery.extend({
  status: z21.enum(["open", "closed"]).optional(),
  operatorId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});
var posApprovalInput = z21.object({ approverUserId: uuid, pin: z21.string().regex(/^\d{4,8}$/, "PIN inv\xE1lido") });
var posDiscountInput = z21.discriminatedUnion("kind", [
  z21.object({ kind: z21.literal("percent"), value: z21.number().int().min(0).max(1e4) }),
  z21.object({ kind: z21.literal("amount"), value: cents })
]);
var posQuoteInput = z21.object({
  productIds: z21.array(uuid).min(1, "Adicione ao menos uma pe\xE7a").max(50),
  itemDiscounts: z21.array(z21.object({ productId: uuid, discount: posDiscountInput })).max(50).default([]),
  totalDiscount: posDiscountInput.nullish(),
  customerId: uuid.nullish(),
  couponCode: z21.string().trim().min(1).max(40).optional(),
  redeemPoints: z21.number().int().min(0).max(1e6).optional()
});
var posPaymentInput = z21.object({
  method: posPaymentMethod,
  /** valor aplicado à venda (sem o troco) */
  amountCents: cents.min(1),
  /** dinheiro: quanto o cliente entregou (>= valor aplicado; a diferença é o troco) */
  tenderedCents: cents.optional(),
  /** cartão (maquininha externa) */
  brand: z21.string().trim().max(30).optional(),
  nsu: z21.string().trim().max(40).optional(),
  installments: z21.number().int().min(1).max(12).optional(),
  /** PIX/transferência: o operador confirma que o valor caiu na conta */
  confirmed: z21.boolean().optional()
});
var posSaleInput = posQuoteInput.extend({
  payments: z21.array(posPaymentInput).min(1, "Informe a forma de pagamento").max(6),
  note: optionalText(300),
  approval: posApprovalInput.nullish()
});
var posProductSearchQuery = z21.object({ q: z21.string().trim().min(1).max(80) });
var posCartItemInput = z21.object({ productId: uuid });
var posCartRenewInput = z21.object({ productIds: z21.array(uuid).min(1).max(50) });
var posSaleListQuery = paginationQuery.extend({
  shiftId: uuid.optional(),
  operatorId: uuid.optional(),
  status: csvOf(POS_SALE_STATUSES).optional(),
  method: csvOf(POS_PAYMENT_METHODS).optional(),
  from: isoDate.optional(),
  to: isoDate.optional()
});
var posReceiptFormatQuery = z21.object({ format: z21.enum(["80mm", "a4"]).default("80mm") });
var posReceiptSendInput = z21.object({ channel: z21.enum(["whatsapp", "email"]) });
var reason2 = (min) => z21.string().trim().min(min, `Informe a justificativa (m\xEDn. ${min} caracteres)`).max(500);
var posCancelInput = z21.object({
  reason: reason2(5),
  /** true = a peça já saiu da loja: vai para devolução com inspeção em vez de voltar direto ao estoque */
  pieceLeft: z21.boolean().default(false),
  refundMethod: posPaymentMethod.optional()
});
var posReturnInput = z21.object({
  productIds: z21.array(uuid).min(1, "Selecione ao menos uma pe\xE7a").max(50),
  reason: reason2(5),
  refundMethod: posPaymentMethod.optional()
});
var posInspectInput = z21.object({ approved: z21.boolean(), note: reason2(3) });
var posReturnListQuery = paginationQuery.extend({ pending: z21.enum(["true", "false"]).optional() });
var posHoldInput = z21.object({
  label: z21.string().trim().max(60).optional(),
  productIds: z21.array(uuid).min(1).max(50),
  customerId: uuid.nullish(),
  note: optionalText(300)
});
var POS_REPORT_KINDS = ["sales", "discounts", "cancellations"];
var posReportQuery = z21.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  operatorId: uuid.optional(),
  shiftId: uuid.optional(),
  method: posPaymentMethod.optional(),
  kind: z21.enum(POS_REPORT_KINDS).default("sales"),
  groupBy: z21.enum(["operator", "shift", "method", "day"]).default("operator")
});

// src/reports.ts
import { z as z22 } from "zod";
var REPORT_KEYS = [
  "sales_period",
  "sales_category",
  "sales_brand",
  "sales_size",
  "products_top",
  "products_margin",
  "stale_stock",
  "margin_piece",
  "stock_value",
  "discounts",
  "channels",
  "campaigns",
  "recurring_customers",
  "consignors",
  "time_to_sale",
  "return_rate"
];
var reportKey = z22.enum(REPORT_KEYS);
var REPORT_COLUMN_TYPES = ["text", "money", "int", "pct", "date", "days"];
var REPORT_FILTER_KEYS = ["period", "channel", "categoryId", "brandId", "size", "origin", "groupBy", "staleDays", "minOrders"];
var day3 = isoDate.refine((v) => !Number.isNaN(Date.parse(v)), "Data inv\xE1lida");
var reportQuery = paginationQuery.extend({
  from: day3.optional(),
  to: day3.optional(),
  channel: orderChannel.optional(),
  categoryId: uuid.optional(),
  brandId: uuid.optional(),
  size: z22.string().trim().max(20).optional(),
  origin: z22.enum(["compra_propria", "consignacao", "outra"]).optional(),
  /** agrupamento/dimensão; valores válidos por relatório no catálogo (inválido = padrão do relatório) */
  groupBy: z22.string().trim().max(20).optional(),
  staleDays: z22.coerce.number().int().min(1).max(3650).default(60),
  minOrders: z22.coerce.number().int().min(1).max(1e3).default(2)
}).refine((v) => !v.from || !v.to || v.from <= v.to, { message: "Per\xEDodo inv\xE1lido", path: ["to"] }).refine((v) => !v.from || !v.to || (Date.parse(v.to) - Date.parse(v.from)) / 864e5 <= 366, { message: "Per\xEDodo m\xE1ximo de 366 dias", path: ["to"] });
export {
  ACCOUNT_TYPE_LABELS,
  AI_ACTION_KINDS,
  AI_ACTION_LABEL,
  AI_DRAFT_KINDS,
  AI_FIELD_LABEL,
  AI_PRODUCT_FIELDS,
  AI_PRODUCT_UNCONFIRMED_ERROR_CODE,
  ALL_PERMISSIONS,
  BARCODE_REGEX,
  BATCH_STATUSES,
  BATCH_STATUS_LABELS,
  BILLING_EVENT_STATUSES,
  BILL_STATUS_LABELS,
  CATEGORY_KIND_LABELS,
  CATEGORY_ROLES,
  CHANNEL_LABELS,
  COMMISSION_TYPES,
  COMM_CHANNELS,
  COMM_STATUSES,
  COMM_STATUS_LABEL,
  COMM_VARIABLES,
  CONDITIONS,
  CONDITION_LABEL,
  CONSENT_TYPES,
  CONSIGNOR_STATUSES,
  CONSIGNOR_STATUS_LABELS,
  COUPON_STATUSES,
  COUPON_VIEW_STATUSES,
  DEFAULT_ORDER_TEMPLATES,
  DEFAULT_POS_DISCOUNT_LIMIT,
  DEFAULT_RETURN_POLICY,
  DEFAULT_ROLES,
  DEFAULT_WHATSAPP_TEMPLATES,
  DELIVERY_METHODS,
  DELIVERY_STATUSES,
  DEPARTMENTS,
  DEPARTMENT_LABEL,
  DISCOUNT_TYPES,
  ENTRY_KIND_LABELS,
  FEATURE_UNAVAILABLE_ERROR_CODE,
  FEE_MODES,
  FEE_MODE_LABELS,
  FINANCE_CHANNELS,
  FREQUENCY_LABELS,
  GRANULARITIES,
  IMPERSONATION_TTL_MINUTES,
  IMPORT_COLUMNS,
  INSPECTION_LABELS,
  INSPECTION_STATUSES,
  IN_STOCK_STATUSES,
  ITEM_STATUSES,
  ITEM_STATUS_LABELS,
  LEDGER_KINDS,
  LEDGER_KIND_LABELS,
  LEGAL_VERSION,
  LIMIT_KEYS,
  LIMIT_LABEL,
  LOCATION_KINDS,
  LOCATION_KIND_LABEL,
  LOYALTY_ENTRY_KINDS,
  LOYALTY_ENTRY_LABEL,
  MARKETING_SCOPES,
  MARKETING_SCOPE_LABEL,
  MEASUREMENT_LABEL,
  MOVEMENT_LABEL,
  MOVEMENT_TYPES,
  NATURE_LABELS,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPES,
  ONBOARDING_STEPS,
  ORDER_CHANNELS,
  ORDER_CHANNEL_LABELS,
  ORDER_DELIVERY_LABELS,
  ORDER_NOTIFY_EVENTS,
  ORDER_NOTIFY_LABELS,
  ORDER_OPEN_STATUSES,
  ORDER_PAID_STATUSES,
  ORDER_STATUSES,
  ORDER_STATUS_LABELS,
  ORDER_TEMPLATE_VARIABLES,
  ORDER_TRANSITIONS,
  ORG_STATUSES,
  ORG_STATUS_LABEL,
  ORG_SUSPENDED_ERROR_CODE,
  ORIGINS,
  ORIGIN_LABEL,
  PAYMENT_ENVIRONMENTS,
  PAYMENT_MANUAL_METHODS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_PICKUP_ONLY_METHODS,
  PAYMENT_PROVIDERS,
  PAYMENT_PROVIDER_METHODS,
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABELS,
  PAYOUT_METHODS,
  PAYOUT_METHOD_LABELS,
  PERMISSIONS,
  PIECE_RESOLUTIONS,
  PIECE_RESOLUTION_LABELS,
  PIX_KEY_TYPES,
  PLAN_INTERVALS,
  PLAN_LIMIT_ERROR_CODE,
  PLATFORM_CREDENTIALS,
  PLATFORM_INVOICE_STATUSES,
  PLATFORM_ORG_SORTS,
  PLATFORM_ROLES,
  PLATFORM_ROLE_LABEL,
  PLATFORM_SUBSCRIPTION_SORTS,
  PLATFORM_USER_SORTS,
  PLATFORM_USER_STATUSES,
  PLATFORM_USER_STATUS_LABEL,
  PORTAL_ACCOUNT_STATUSES,
  POS_NO_FISCAL_TEXT,
  POS_PAYMENT_LABELS,
  POS_PAYMENT_METHODS,
  POS_REPORT_KINDS,
  POS_SALE_STATUSES,
  POS_SALE_STATUS_LABELS,
  PRODUCT_SORT_KEYS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABEL,
  PRODUCT_TRANSITIONS,
  PROMOTION_STATUSES,
  PROMOTION_VIEW_STATUSES,
  PURCHASE_ALLOCATION_LABELS,
  PURCHASE_ALLOCATION_METHODS,
  PURCHASE_COST_KINDS,
  PURCHASE_COST_LABELS,
  PURCHASE_SORT_KEYS,
  PURCHASE_STATUSES,
  PURCHASE_STATUS_LABELS,
  RECORD_ACCESS_ACTIONS,
  REMOVED_EMAIL_DOMAIN,
  REPORT_COLUMN_TYPES,
  REPORT_FILTER_KEYS,
  REPORT_KEYS,
  RESERVATION_CHANNELS,
  RETURN_ACTIVE_STATUSES,
  RETURN_CHANNELS,
  RETURN_CHANNEL_LABELS,
  RETURN_CONDITIONS,
  RETURN_CONDITION_LABELS,
  RETURN_DECISIONS,
  RETURN_DECISION_LABELS,
  RETURN_LEGAL,
  RETURN_LEGAL_REASONS,
  RETURN_MANUAL_METHODS,
  RETURN_MANUAL_METHOD_LABELS,
  RETURN_REASONS,
  RETURN_REASON_LABELS,
  RETURN_REFUND_METHODS,
  RETURN_RESOLUTIONS,
  RETURN_RESOLUTION_LABELS,
  RETURN_SHIPPING_PAYERS,
  RETURN_SHIPPING_PAYER_LABELS,
  RETURN_STATUSES,
  RETURN_STATUS_LABELS,
  RETURN_TERMINAL,
  RETURN_TRANSITIONS,
  SALES_CHANNELS,
  SALES_CHANNEL_LABEL,
  SETTLEMENT_STATUSES,
  SETTLEMENT_STATUS_LABELS,
  SHIPPING_KINDS,
  SHIPPING_KIND_LABELS,
  SHIPPING_PROVIDER_KEYS,
  SKU_REGEX,
  STANDARD_SIZES,
  STOREFRONT_DEFAULT_BENEFITS,
  STOREFRONT_DEFAULT_HERO_SUBTITLE,
  STOREFRONT_DEFAULT_SECTIONS,
  STOREFRONT_FONT_PAIRS,
  STOREFRONT_FONT_PAIR_LABEL,
  STOREFRONT_HERO_HEIGHTS,
  STOREFRONT_HERO_LAYOUTS,
  STOREFRONT_ICONS,
  STOREFRONT_PRESETS,
  STOREFRONT_ROUTING_STATUSES,
  STOREFRONT_SECTION_KEYS,
  STOREFRONT_SORTS,
  STORE_CREDIT_KINDS,
  STORE_CREDIT_KIND_LABELS,
  SUBSCRIPTION_ACTIONS,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_LABEL,
  SUPPLIER_SORT_KEYS,
  SUSPEND_POLICIES,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABEL,
  TICKET_STATUSES,
  TICKET_STATUS_LABEL,
  WHATSAPP_REMINDER_HOURS,
  WHATSAPP_STATUSES,
  WHATSAPP_TEMPLATE_KINDS,
  WHATSAPP_TEMPLATE_LABEL,
  WHATSAPP_TEMPLATE_MAX,
  WHATSAPP_UNAVAILABLE_MESSAGE,
  WHATSAPP_VARIABLES,
  WHATSAPP_VARIABLE_KEYS,
  acceptInviteInput,
  acceptTermsField,
  acceptTermsInput,
  accountInput,
  accountType,
  addressSchema,
  adjustInput,
  agingQuery,
  aiActionDecisionInput,
  aiAltField,
  aiAssistantMessageInput,
  aiAuditQuery,
  aiChatInput,
  aiConfirmFieldsInput,
  aiConversationCreateInput,
  aiDraftInput,
  aiFieldDecision,
  aiProductBatchInput,
  aiProductSuggestInput,
  aiSearchInput,
  aiSuggestionReviewInput,
  auditQuery,
  billInput,
  billStatus,
  billType,
  billingEventsQuery,
  billsExportQuery,
  billsListQuery,
  branchInput,
  brandInput,
  businessHoursInput,
  businessHoursInterval,
  canOrderTransition,
  canReturnTransition,
  canTransition,
  cancelBillInput,
  cancelSubscriptionInput,
  cashFlowQuery,
  categoryInput,
  categoryKind,
  categoryRole,
  cents,
  cepBR,
  cepLookupResponse,
  changeEmailInput,
  changePasswordInput,
  changePlanInput,
  checkoutAddressInput,
  checkoutGuestInput,
  checkoutOrderInput,
  checkoutQuoteInput,
  checkoutRetryPaymentInput,
  closeInput,
  cnpjBR,
  commCampaignInput,
  commCampaignListQuery,
  commChannel,
  commScheduleInput,
  compareQuery,
  confirmDeletionInput,
  consentInput,
  consentType,
  consignmentBatchInput,
  consignmentBatchListQuery,
  consignmentBatchSignedInput,
  consignmentBatchStatus,
  consignmentBatchUpdateInput,
  consignmentCommissionType,
  consignmentDiscountRule,
  consignmentFeeMode,
  consignmentItemInput,
  consignmentItemListQuery,
  consignmentItemStatus,
  consignmentNewProduct,
  consignmentOverdueExportQuery,
  consignmentOverdueQuery,
  consignmentPayoutInput,
  consignmentPayoutListQuery,
  consignmentPayoutMethod,
  consignmentReturnInput,
  consignmentReturnListQuery,
  consignmentReturnRule,
  consignmentSettingsInput,
  consignmentSettlementInput,
  consignmentSettlementListQuery,
  consignmentSettlementStatus,
  consignorAdjustmentInput,
  consignorDocument,
  consignorExportQuery,
  consignorInput,
  consignorLedgerKind,
  consignorListQuery,
  consignorPaymentInput,
  consignorPixKeyType,
  consignorStatementExportQuery,
  consignorStatementQuery,
  consignorStatus,
  contactFormBlock,
  contactMessageInput,
  contactMessagesQuery,
  countCloseInput,
  countItemSetInput,
  countItemsQuery,
  countListQuery,
  countScanInput,
  countStartInput,
  couponCode2 as couponCode,
  couponExportQuery,
  couponFilters,
  couponInput,
  couponListQuery,
  couponStatus,
  cpfBR,
  createPageInput,
  ctaBlock,
  customerAddressInput,
  customerDuplicateQuery,
  customerExportQuery,
  customerFilters,
  customerInput,
  customerListQuery,
  customerPreferencesInput,
  dashboardQuery,
  deliveryExportQuery,
  deliveryListQuery,
  discountType,
  dreByChannelQuery,
  dreQuery,
  emailSchema,
  entriesExportQuery,
  entriesListQuery,
  entryInput,
  entryKind,
  expenseNature,
  extendTrialInput,
  faqBlock,
  featureFlagInput,
  featureOverrideInput,
  financeCategoryInput,
  financeChannel,
  financeOverviewQuery,
  financePeriodQuery,
  forgotPasswordInput,
  galleryBlock,
  heroBlock,
  idempotencyKey,
  idempotencyKeyHeader,
  impersonateInput,
  importCommitInput,
  importConfirmInput,
  importPreviewInput,
  inspectionStatus,
  inventorySettingsInput,
  inviteMemberInput,
  isAiAltField,
  isValidCnpj,
  isValidCpf,
  isoDate,
  isoDateTime,
  labelsInput,
  limitValue,
  locationBlock,
  locationInput,
  loginInput,
  loyaltyAdjustInput,
  loyaltyLedgerQuery,
  loyaltyTier,
  manualTargets,
  markMessageReadInput,
  marketingScope,
  marketingSettingsInput,
  matchInput,
  measurementsSchema,
  monthlyQuery,
  movementExportQuery,
  movementQuery,
  noHtml,
  onboardingStateInput,
  onboardingStatus,
  onlyDigits,
  optionalText,
  orderCancelInput,
  orderChannel,
  orderConfirmPaymentInput,
  orderDeliveryMethod,
  orderListQuery,
  orderSettingsInput,
  orderStatus,
  orderStatusInput,
  orderTemplatesInput,
  orgFeatureOverrideInput,
  organizationSettingsInput,
  pageContentSchema,
  pageSlug,
  paginationQuery,
  passwordSchema,
  paymentCredentialsInput,
  paymentMethod,
  paymentProviderKey,
  paymentRefundInput,
  paymentSettingsInput,
  periodReportQuery,
  phoneBR,
  pieceResolution,
  planLimitsInput,
  platformAdminInput,
  platformAdminUpdateInput,
  platformAuditQuery,
  platformInvoicesQuery,
  platformMemberUpdateInput,
  platformMetricsQuery,
  platformOrgCreateInput,
  platformOrgListQuery,
  platformOrgUserInput,
  platformPlanInput,
  platformSettingsInput,
  platformSubscriptionListQuery,
  platformUsageQuery,
  platformUserDeleteInput,
  platformUserSetPasswordInput,
  platformUserUpdateInput,
  platformUsersQuery,
  portalAccountsQuery,
  portalActivateInput,
  portalCommunicationInput,
  portalConsentInput,
  portalForgotPasswordInput,
  portalInviteInput,
  portalLoginInput,
  portalProfileUpdateInput,
  portalResetPasswordInput,
  portalTokenInput,
  posApprovalInput,
  posCancelInput,
  posCartItemInput,
  posCartRenewInput,
  posCashMovementInput,
  posCloseShiftInput,
  posDiscountInput,
  posHoldInput,
  posInspectInput,
  posOpenShiftInput,
  posPaymentInput,
  posPaymentMethod,
  posPinInput,
  posProductSearchQuery,
  posQuoteInput,
  posReceiptFormatQuery,
  posReceiptSendInput,
  posReportQuery,
  posReturnInput,
  posReturnListQuery,
  posSaleInput,
  posSaleListQuery,
  posSettingsInput,
  posShiftListQuery,
  postLineSchema,
  pricingItem,
  pricingQuoteInput,
  productBulkInput,
  productCondition,
  productDepartment,
  productExportQuery,
  productFilters,
  productImageReorder,
  productImageUpdate,
  productInput,
  productListQuery,
  productMargin,
  productOrigin,
  productStatus,
  productStatusInput,
  promoCampaignInput,
  promoCampaignListQuery,
  promotionExportQuery,
  promotionFilters,
  promotionInput,
  promotionListQuery,
  promotionStatus,
  publishedListQuery,
  purchaseAllocationMethod,
  purchaseCancelInput,
  purchaseCostInput,
  purchaseCostKind,
  purchaseExportQuery,
  purchaseFilters,
  purchaseImportCommitInput,
  purchaseInput,
  purchaseInstallmentInput,
  purchaseInstallmentPlan,
  purchaseInstallmentsInput,
  purchaseItemInput,
  purchaseLabelsInput,
  purchaseListQuery,
  purchasePayInstallmentInput,
  purchaseReceiveInput,
  purchaseReceivePiece,
  purchaseRejectInput,
  purchaseStatus,
  reconciliationQuery,
  recordAccessQuery,
  recurrenceFrequency,
  recurrenceInput,
  registerInput,
  renderOrderTemplate,
  renderWhatsappTemplate,
  reopenInput,
  reorderPagesInput,
  reportKey,
  reportQuery,
  resetPasswordInput,
  resolveStorefrontHome,
  restoreVersionInput,
  returnCancelByCustomerInput,
  returnCondition,
  returnCreateInput,
  returnCustomerCreateInput,
  returnDecision,
  returnInspectionInput,
  returnListQuery,
  returnMessageInput,
  returnPolicyInput,
  returnPolicyViolations,
  returnReason,
  returnReceiveInput,
  returnReportQuery,
  returnResolution,
  returnResolveInput,
  returnShippingBearer,
  returnStatus,
  returnTransitionInput,
  returnWindowDays,
  reverseInput,
  reviewInput,
  reviewsBlock,
  roleInput,
  safeUrl,
  salesChannel,
  saveDraftInput,
  scheduleDeletionInput,
  segmentInput,
  segmentListQuery,
  segmentMembersQuery,
  segmentRules,
  seoSchema,
  settleInput,
  shippingMethodInput,
  shippingZoneInput,
  signedCents,
  siteBlock,
  siteThemeInput,
  siteUrl,
  slugSchema,
  sortOrder,
  staleQuery,
  statementQuery,
  stockAdjustInput,
  stockEntryInput,
  stockMoveLocationInput,
  stockReserveManualInput,
  stockWithdrawInput,
  stockWriteOffInput,
  storeCreditAdjustInput,
  storefrontAddressesInput,
  storefrontAlertInput,
  storefrontBannerInput,
  storefrontBenefitInput,
  storefrontBenefitsInput,
  storefrontCardsQuery,
  storefrontCatalogQuery,
  storefrontContactInput,
  storefrontFavoriteMergeInput,
  storefrontHeroInput,
  storefrontHomeInput,
  storefrontHostname,
  storefrontPreferencesInput,
  storefrontRegisterInput,
  storefrontSectionInput,
  storefrontSettingsInput,
  supplierAddressInput,
  supplierContactInput,
  supplierDocument,
  supplierExportQuery,
  supplierFilters,
  supplierInput,
  supplierListQuery,
  suspendOrgInput,
  textBlock,
  ticketCreateInput,
  ticketListQuery,
  ticketMessageInput,
  ticketUpdateInput,
  timeHHmm,
  transferInput,
  turnoverQuery,
  unknownWhatsappVariables,
  unsubscribeInput,
  unsubscribeListQuery,
  updateMemberInput,
  updatePageMetaInput,
  updateProfileInput,
  uuid,
  weekday,
  whatsappSettingsInput,
  whatsappTestInput
};
